import type { Order, OrderStatus, PaymentStatus } from "@/services/orders";

export type AdminUser = {
  AdminUserId: string;
  Username: string;
  DisplayName: string;
  Role: string;
  RoleLabel?: string;
  IsActive?: boolean;
  /** 超級管理者略過所有頁面權限檢查，且只有超級管理者能維護選單結構或勾選其他帳號的權限。 */
  IsSuperAdmin?: boolean;
  /** 這個帳號目前擁有的頁面權限鍵值清單（超級管理者的話後端會回傳全部鍵值）。 */
  Permissions?: string[];
  LastLoginAt?: string | null;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

/** 後台「頁面權限」固定目錄，跟後端 AdminPermissionCatalog 一致，由 /api/admin/users 回應附帶。 */
export type AdminPermissionCatalogItem = { Key: string; Label: string; Path: string };

export type ReservationStatus = "New" | "Contacted" | "Booked" | "Closed";

export type PaginationMeta = {
  Page: number;
  PerPage: number;
  Total: number;
  TotalPages: number;
};

export type Reservation = {
  ReservationId: string;
  Name: string;
  Phone: string;
  Store: string;
  ContactTime: string;
  Note: string | null;
  Campaign: string;
  PageUrl: string | null;
  IpAddress: string | null;
  Status: ReservationStatus;
  CreatedAt: string;
  UpdatedAt: string;
};

export type ProductTrialReservation = {
  ReservationId: string;
  Name: string;
  Phone: string;
  Store: string;
  AppointmentDate: string;
  AppointmentTime: string;
  ProductId: string | null;
  ProductName: string;
  ProductCode: string | null;
  Note: string | null;
  PageUrl: string | null;
  IpAddress: string | null;
  Status: ReservationStatus;
  CreatedAt: string;
  UpdatedAt: string;
};

type ApiResponse<T> = T & {
  ok: boolean;
  message?: string;
  errors?: Record<string, string>;
};

export class AdminApiError extends Error {
  errors: Record<string, string>;
  status: number;

  constructor(message: string, errors: Record<string, string> = {}, status = 0) {
    super(message);
    this.name = "AdminApiError";
    this.errors = errors;
    this.status = status;
  }
}

const csrfKey = "fulling_admin_csrf";

export function getCsrfToken() {
  return sessionStorage.getItem(csrfKey) || "";
}

function setCsrfToken(token?: string) {
  if (token) {
    sessionStorage.setItem(csrfKey, token);
  }
}

export function clearCsrfToken() {
  sessionStorage.removeItem(csrfKey);
}

type AdminRequestOptions = {
  body?: string;
  headers?: Record<string, string>;
  method?: string;
};

async function request<T>(path: string, options: AdminRequestOptions = {}) {
  const response = await fetch(path, {
    credentials: "include",
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(getCsrfToken() ? { "X-CSRF-Token": getCsrfToken() } : {}),
      ...(options.headers || {}),
    },
  });
  const data = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (data && "CsrfToken" in data) {
    setCsrfToken(String(data.CsrfToken || ""));
  }
  if (!response.ok || data.ok === false) {
    throw new AdminApiError(data.message || "操作失敗，請稍後再試。", data.errors || {}, response.status);
  }
  return data;
}

export async function fetchCsrf() {
  const data = await request<{ CsrfToken: string }>("/api/admin/csrf");
  setCsrfToken(data.CsrfToken);
  return data.CsrfToken;
}

export async function login(Username: string, Password: string) {
  if (!getCsrfToken()) {
    await fetchCsrf();
  }
  return request<{ User: AdminUser; CsrfToken: string }>("/api/admin/login", {
    method: "POST",
    body: JSON.stringify({ Username, Password }),
  });
}

export async function getCurrentAdmin() {
  return request<{ User: AdminUser; CsrfToken: string }>("/api/admin/me");
}

export async function logout() {
  const result = await request<Record<string, never>>("/api/admin/logout", { method: "POST" });
  clearCsrfToken();
  return result;
}

type ListParams = {
  Status?: string;
  Keyword?: string;
  Page?: number;
  PerPage?: number;
};

export async function listReservations(params: ListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Reservations: Reservation[];
    Counts?: Record<ReservationStatus, number>;
    Pagination: PaginationMeta;
  }>(`/api/admin/reservations${suffix}`);
}

export async function exportReservations(params: Pick<ListParams, "Status" | "Keyword">) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(`/api/admin/reservations/export${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null) as { message?: string } | null;
    throw new Error(data?.message || "匯出失敗，請稍後再試。");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const matched = disposition.match(/filename=\"?([^\";]+)\"?/i);
  const filename = matched?.[1] || `Reservations-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function updateReservationStatus(ReservationId: string, Status: ReservationStatus) {
  return request<{ Reservation: Reservation }>(`/api/admin/reservations/${ReservationId}/status`, {
    method: "POST",
    body: JSON.stringify({ Status }),
  });
}

export async function listProductTrialReservations(params: ListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Reservations: ProductTrialReservation[];
    Counts?: Record<ReservationStatus, number>;
    Pagination: PaginationMeta;
  }>(`/api/admin/product-trial-reservations${suffix}`);
}

export async function exportProductTrialReservations(params: Pick<ListParams, "Status" | "Keyword">) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(`/api/admin/product-trial-reservations/export${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null) as { message?: string } | null;
    throw new Error(data?.message || "匯出失敗，請稍後再試。");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const matched = disposition.match(/filename=\"?([^\";]+)\"?/i);
  const filename = matched?.[1] || `ProductTrialReservations-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function updateProductTrialReservationStatus(ReservationId: string, Status: ReservationStatus) {
  return request<{ Reservation: ProductTrialReservation }>(`/api/admin/product-trial-reservations/${ReservationId}/status`, {
    method: "POST",
    body: JSON.stringify({ Status }),
  });
}

export type ContactMessageStatus = "New" | "Replied" | "Closed";

export type ContactMessage = {
  ContactMessageId: string;
  Name: string;
  Email: string;
  Subject: string;
  Message: string;
  PageUrl: string | null;
  IpAddress: string | null;
  Status: ContactMessageStatus;
  CreatedAt: string;
  UpdatedAt: string;
};

export async function listContactMessages(params: ListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Messages: ContactMessage[];
    Counts?: Record<ContactMessageStatus, number>;
    Pagination: PaginationMeta;
  }>(`/api/admin/contact-messages${suffix}`);
}

export async function updateContactMessageStatus(ContactMessageId: string, Status: ContactMessageStatus) {
  return request<{ Message: ContactMessage }>(`/api/admin/contact-messages/${ContactMessageId}/status`, {
    method: "POST",
    body: JSON.stringify({ Status }),
  });
}

export async function deleteContactMessage(ContactMessageId: string) {
  return request<Record<string, never>>(`/api/admin/contact-messages/${ContactMessageId}`, {
    method: "DELETE",
  });
}

export type CustomerServiceTicketStatus = "New" | "InProgress" | "WaitingCustomer" | "WaitingInternal" | "Resolved" | "Closed";
export type CustomerServiceTicketPriority = "Low" | "Normal" | "High" | "Urgent";
export type CustomerServiceTicketCategory = "General" | "Product" | "Reservation" | "Order" | "Member" | "AfterSales";
export type CustomerServiceSourceType = "Manual" | "ProductTrial" | "Reservation" | "ContactMessage" | "Order" | "Member";
export type CustomerServiceActivityType = "Note" | "Call" | "Email" | "Customer" | "StatusChange" | "Assignment" | "FollowUp" | "System";

export type CustomerServiceTicket = {
  TicketId: string;
  TicketNumber: string;
  SourceType: CustomerServiceSourceType;
  SourceId: string | null;
  Subject: string;
  Category: CustomerServiceTicketCategory;
  Priority: CustomerServiceTicketPriority;
  Status: CustomerServiceTicketStatus;
  CustomerName: string | null;
  CustomerPhone: string | null;
  CustomerEmail: string | null;
  MemberId: string | null;
  AssignedAdminUserId: string | null;
  AssignedAdminName: string | null;
  LastContactedAt: string | null;
  NextFollowUpAt: string | null;
  ClosedAt: string | null;
  SourceSummary: Record<string, unknown> | null;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type CustomerServiceActivity = {
  ActivityId: string;
  TicketId: string;
  AdminUserId: string | null;
  AdminDisplayName: string | null;
  ActivityType: CustomerServiceActivityType;
  Visibility: "Internal" | "Customer";
  Body: string;
  Metadata: Record<string, unknown> | null;
  CreatedAt: string | null;
};

export type CustomerServiceAssignee = {
  AdminUserId: string;
  Username: string;
  DisplayName: string;
};

export type CustomerServiceSourceOption = {
  SourceType: CustomerServiceSourceType;
  SourceId: string;
  Label: string;
  Description: string;
  Status: string | null;
  CustomerName: string | null;
  CustomerPhone: string | null;
  CustomerEmail: string | null;
  CreatedAt: string | null;
};

export type CustomerServiceTicketPayload = {
  SourceType?: CustomerServiceSourceType;
  SourceId?: string | null;
  Subject?: string;
  Category?: CustomerServiceTicketCategory;
  Priority?: CustomerServiceTicketPriority;
  Status?: CustomerServiceTicketStatus;
  CustomerName?: string;
  CustomerPhone?: string;
  CustomerEmail?: string;
  MemberId?: string | null;
  AssignedAdminUserId?: string | null;
  NextFollowUpAt?: string | null;
  InitialNote?: string;
};

export type CustomerServiceTicketListParams = {
  Status?: CustomerServiceTicketStatus | "";
  Priority?: CustomerServiceTicketPriority | "";
  SourceType?: CustomerServiceSourceType | "";
  AssignedAdminUserId?: string;
  Keyword?: string;
  Overdue?: boolean;
  Page?: number;
  PerPage?: number;
};

function customerServiceTicketQuery(params: CustomerServiceTicketListParams = {}) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Priority) query.set("Priority", params.Priority);
  if (params.SourceType) query.set("SourceType", params.SourceType);
  if (params.AssignedAdminUserId) query.set("AssignedAdminUserId", params.AssignedAdminUserId);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Overdue) query.set("Overdue", "1");
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  return query.toString() ? `?${query.toString()}` : "";
}

export async function listCustomerServiceTickets(params: CustomerServiceTicketListParams = {}) {
  return request<{
    Tickets: CustomerServiceTicket[];
    Counts: { Status: Record<CustomerServiceTicketStatus, number>; Priority: Record<CustomerServiceTicketPriority, number>; Overdue: number };
    Pagination: PaginationMeta;
  }>(`/api/admin/customer-service/tickets${customerServiceTicketQuery(params)}`);
}

export async function getCustomerServiceTicket(ticketId: string) {
  return request<{ Ticket: CustomerServiceTicket; Activities: CustomerServiceActivity[] }>(`/api/admin/customer-service/tickets/${ticketId}`);
}

export async function createCustomerServiceTicket(payload: CustomerServiceTicketPayload) {
  return request<{ Ticket: CustomerServiceTicket }>("/api/admin/customer-service/tickets", { method: "POST", body: JSON.stringify(payload) });
}

export async function updateCustomerServiceTicket(ticketId: string, payload: CustomerServiceTicketPayload) {
  return request<{ Ticket: CustomerServiceTicket; Activities: CustomerServiceActivity[] }>(`/api/admin/customer-service/tickets/${ticketId}`, { method: "PATCH", body: JSON.stringify(payload) });
}

export async function createCustomerServiceActivity(ticketId: string, payload: { ActivityType: CustomerServiceActivityType; Body: string; Visibility?: "Internal" | "Customer" }) {
  return request<{ Ticket: CustomerServiceTicket; Activities: CustomerServiceActivity[] }>(`/api/admin/customer-service/tickets/${ticketId}/activities`, { method: "POST", body: JSON.stringify(payload) });
}

export async function searchCustomerServiceSourceOptions(SourceType: CustomerServiceSourceType, Keyword = "") {
  const query = new URLSearchParams({ SourceType });
  if (Keyword.trim()) query.set("Keyword", Keyword.trim());
  return request<{ Options: CustomerServiceSourceOption[] }>(`/api/admin/customer-service/source-options?${query.toString()}`);
}

export async function listCustomerServiceAssignees() {
  return request<{ Users: CustomerServiceAssignee[] }>("/api/admin/customer-service/assignees");
}

export type AdminUserPayload = {
  Username?: string;
  DisplayName: string;
  Role: string;
  IsActive: boolean;
  Password?: string;
  /** 只有目前登入的帳號是超級管理者時，這兩個欄位才會被後端接受，否則整個請求會被拒絕（見後端說明）。 */
  IsSuperAdmin?: boolean;
  Permissions?: string[];
};

export type AdminUserRoleOption = { Key: string; Label: string };

export type AdminUserListParams = ListParams & { Role?: string };

export async function listAdminUsers(params: AdminUserListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Role) query.set("Role", params.Role);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Users: AdminUser[];
    Counts?: { Active: number; Inactive: number };
    RoleCounts?: Record<string, number>;
    Pagination: PaginationMeta;
    PermissionCatalog: AdminPermissionCatalogItem[];
    RoleOptions: AdminUserRoleOption[];
    IsActingSuperAdmin: boolean;
  }>(`/api/admin/users${suffix}`);
}

export async function createAdminUser(payload: AdminUserPayload) {
  return request<{ User: AdminUser }>("/api/admin/users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateAdminUser(AdminUserId: string, payload: AdminUserPayload) {
  return request<{ User: AdminUser }>(`/api/admin/users/${AdminUserId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteAdminUser(AdminUserId: string) {
  return request<Record<string, never>>(`/api/admin/users/${AdminUserId}`, {
    method: "DELETE",
  });
}

export type AdminStoreUserRole = "StoreManager" | "Staff" | "Audiologist" | "Support";

export type AdminStoreUserLocation = {
  LocationId: string;
  Name: string;
  AddressCity?: string | null;
  AddressDistrict?: string | null;
  IsPrimary?: boolean;
  IsActive?: boolean;
};

export type AdminStorePermissionCatalogItem = { Key: string; Label: string; Path: string };
export type AdminStoreRoleOption = { Value: AdminStoreUserRole; Label: string };

export type AdminStoreUser = {
  StoreUserId: string;
  Username: string;
  DisplayName: string;
  Email: string | null;
  Phone: string | null;
  Role: AdminStoreUserRole;
  RoleLabel?: string;
  IsActive: boolean;
  Locations: AdminStoreUserLocation[];
  LocationIds: string[];
  PrimaryLocationId: string | null;
  Permissions: string[];
  LastLoginAt?: string | null;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminStoreUserPayload = {
  Username?: string;
  DisplayName: string;
  Email?: string | null;
  Phone?: string | null;
  Role: AdminStoreUserRole;
  IsActive: boolean;
  Password?: string;
  LocationIds: string[];
  PrimaryLocationId?: string | null;
  Permissions: string[];
};

export type AdminStoreUserListParams = ListParams & { Role?: string; LocationIds?: string[] };

export async function listAdminStoreUsers(params: AdminStoreUserListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Role) query.set("Role", params.Role);
  params.LocationIds?.forEach((LocationId) => query.append("LocationIds[]", LocationId));
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Users: AdminStoreUser[];
    Counts?: { Active: number; Inactive: number };
    Pagination: PaginationMeta;
    Locations: AdminStoreUserLocation[];
    PermissionCatalog: AdminStorePermissionCatalogItem[];
    RoleOptions: AdminStoreRoleOption[];
  }>(`/api/admin/store-users${suffix}`);
}

export async function createAdminStoreUser(payload: AdminStoreUserPayload) {
  return request<{ User: AdminStoreUser }>("/api/admin/store-users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateAdminStoreUser(StoreUserId: string, payload: AdminStoreUserPayload) {
  return request<{ User: AdminStoreUser }>(`/api/admin/store-users/${StoreUserId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteAdminStoreUser(StoreUserId: string) {
  return request<Record<string, never>>(`/api/admin/store-users/${StoreUserId}`, {
    method: "DELETE",
  });
}

export type AdminStoreSalesOrderStatus = string;

export type AdminStoreSalesOrderPaymentStatus = "Unpaid" | "DepositPaid" | "Paid" | "BalanceDue";
export type AdminStoreSalesOrderFulfillmentType = "Headquarters" | "StoreStock";
export type AdminStoreSalesOrderOption = { Key: string; Label: string };

export type AdminStoreSalesOrderItem = {
  StoreSalesOrderItemId: string;
  ProductId: string;
  VariantId: string | null;
  ProductName: string;
  VariantLabel: string | null;
  Sku: string | null;
  UnitPrice: number;
  DealPrice: number;
  Quantity: number;
  LineTotal: number;
  SerialNumber: string | null;
  EarSide: string | null;
  WarrantyStartDate: string | null;
  CreatedAt: string | null;
};

export type AdminStoreSalesOrderPayment = {
  StoreSalesOrderPaymentId: string;
  ParentPaymentId?: string | null;
  PaymentType: string;
  PaymentTypeLabel?: string;
  PaymentMethod: string;
  PaymentMethodLabel?: string;
  Amount: number;
  PaymentStatus?: string;
  PaymentStatusLabel?: string;
  ReconciliationStatus?: string;
  ReconciliationStatusLabel?: string;
  ReconciliationNote?: string | null;
  IsAbnormal?: boolean;
  AbnormalNote?: string | null;
  ReviewedAt?: string | null;
  ReviewNote?: string | null;
  VoidedAt?: string | null;
  VoidReason?: string | null;
  StoreUserId: string | null;
  StoreUserName: string | null;
  Note: string | null;
  ReceivedAt: string | null;
  CreatedAt: string | null;
};

export type AdminStoreSalesOrderNotification = {
  StoreSalesOrderNotificationId: string;
  NotificationType: string;
  Method: string;
  RecipientEmail: string;
  AdminUserId?: string | null;
  AdminUserName?: string | null;
  StoreUserId?: string | null;
  StoreUserName?: string | null;
  Subject: string;
  BodySummary: string | null;
  Status: "Sent" | "Failed" | string;
  ErrorMessage: string | null;
  SentAt: string | null;
  CreatedAt: string | null;
};

export type AdminStoreSalesOrderStockDecision = {
  Decision: string;
  Label: string;
  CheckedAt?: string | null;
  Summary?: string | null;
  Items?: Array<{
    ProductName: string;
    VariantLabel?: string | null;
    Sku?: string | null;
    RequiredQuantity: number;
    AvailableQuantity: number;
    ShortageQuantity: number;
  }>;
};

export type AdminStoreSalesOrder = {
  StoreSalesOrderId: string;
  OrderNumber: string;
  LocationId: string;
  LocationName: string | null;
  StoreUserId: string | null;
  StoreUserName: string | null;
  MemberId: string | null;
  CustomerName: string;
  CustomerPhone: string;
  CustomerEmail: string | null;
  Status: AdminStoreSalesOrderStatus;
  StatusLabel: string;
  PaymentStatus: AdminStoreSalesOrderPaymentStatus;
  PaymentStatusLabel: string;
  FulfillmentType: AdminStoreSalesOrderFulfillmentType;
  FulfillmentTypeLabel: string;
  OrderPurpose?: string | null;
  OrderPurposeLabel?: string | null;
  ZeroPriceReason?: string | null;
  ZeroPriceReasonLabel?: string | null;
  ZeroPriceNote?: string | null;
  DepositAmount: number;
  TotalAmount: number;
  BalanceAmount: number;
  InvoiceType: string | null;
  InvoiceTypeLabel: string | null;
  InvoiceStatus: string | null;
  InvoiceStatusLabel: string | null;
  InvoiceTaxId: string | null;
  InvoiceTitle: string | null;
  InvoiceCarrier: string | null;
  InvoiceDonationCode: string | null;
  InvoiceNote: string | null;
  ExpectedPickupDate: string | null;
  DeliveredAt: string | null;
  DeliveredByStoreUserId: string | null;
  DeliveryNote: string | null;
  VoidedAt: string | null;
  VoidedByStoreUserId: string | null;
  VoidReason: string | null;
  CustomerNote: string | null;
  InternalNote: string | null;
  FirstProductName?: string | null;
  FirstVariantLabel?: string | null;
  ItemCount?: number | null;
  Items?: AdminStoreSalesOrderItem[];
  Payments?: AdminStoreSalesOrderPayment[];
  Notifications?: AdminStoreSalesOrderNotification[];
  NextStatusOptions?: AdminStoreSalesOrderOption[];
  StockDecision?: AdminStoreSalesOrderStockDecision | null;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type AdminStoreSalesOrderListParams = {
  Status?: string;
  PaymentStatus?: string;
  FulfillmentType?: string;
  LocationIds?: string[];
  DateFrom?: string;
  DateTo?: string;
  Keyword?: string;
  Page?: number;
  PerPage?: number;
};

function adminStoreSalesOrderQuery(params: AdminStoreSalesOrderListParams = {}) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.PaymentStatus) query.set("PaymentStatus", params.PaymentStatus);
  if (params.FulfillmentType) query.set("FulfillmentType", params.FulfillmentType);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  params.LocationIds?.forEach((LocationId) => query.append("LocationIds[]", LocationId));
  if (params.DateFrom) query.set("DateFrom", params.DateFrom);
  if (params.DateTo) query.set("DateTo", params.DateTo);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  return query.toString() ? `?${query.toString()}` : "";
}

export async function listAdminStoreSalesOrders(params: AdminStoreSalesOrderListParams = {}) {
  return request<{
    Orders: AdminStoreSalesOrder[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    Locations: AdminStoreUserLocation[];
    StatusOptions: AdminStoreSalesOrderOption[];
    PaymentStatusOptions: AdminStoreSalesOrderOption[];
    FulfillmentTypeOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/store-sales-orders${adminStoreSalesOrderQuery(params)}`);
}

export async function getAdminStoreSalesOrder(StoreSalesOrderId: string) {
  return request<{ Order: AdminStoreSalesOrder }>(`/api/admin/store-sales-orders/${StoreSalesOrderId}`);
}

export async function updateAdminStoreSalesOrderStatus(StoreSalesOrderId: string, payload: { Status: AdminStoreSalesOrderStatus; InternalNote?: string }) {
  return request<{ Order: AdminStoreSalesOrder }>(`/api/admin/store-sales-orders/${StoreSalesOrderId}/status`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function autoRouteAdminStoreSalesOrder(StoreSalesOrderId: string, payload: { InternalNote?: string } = {}) {
  return request<{ Order: AdminStoreSalesOrder; Decision?: AdminStoreSalesOrderStockDecision; message?: string }>(`/api/admin/store-sales-orders/${StoreSalesOrderId}/auto-route`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function sendAdminStoreSalesOrderPickupNotification(StoreSalesOrderId: string) {
  return request<{ Order: AdminStoreSalesOrder; message?: string }>(`/api/admin/store-sales-orders/${StoreSalesOrderId}/pickup-notification`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export type AdminStoreOrderStatusItem = {
  Key: string;
  StatusKey: string;
  Label: string;
  SortOrder: number;
  Tone: string;
  VisibleInAdmin: boolean;
  VisibleInStore: boolean;
  IsTerminal: boolean;
  IsSystem: boolean;
  IsActive: boolean;
  Description: string | null;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type AdminStoreOrderStatusTransition = {
  StoreSalesOrderStatusTransitionId: string;
  FromStatusKey: string;
  FromLabel: string;
  ToStatusKey: string;
  ToLabel: string;
  FulfillmentType: string;
  AllowedActor: string;
  RequiresPaymentSettled: boolean;
  RequiresArrivedAtStore: boolean;
  SortOrder: number;
  IsActive: boolean;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type AdminStoreOrderStatusPayload = Partial<Pick<AdminStoreOrderStatusItem, "Label" | "SortOrder" | "Tone" | "VisibleInAdmin" | "VisibleInStore" | "IsTerminal" | "IsActive" | "Description">>;

export type AdminStoreOrderStatusTransitionPayload = {
  FromStatusKey: string;
  ToStatusKey: string;
  FulfillmentType: string;
  AllowedActor: string;
  RequiresPaymentSettled: boolean;
  RequiresArrivedAtStore: boolean;
  SortOrder: number;
  IsActive: boolean;
};

export async function listAdminStoreOrderStatuses() {
  return request<{
    Statuses: AdminStoreOrderStatusItem[];
    Transitions: AdminStoreOrderStatusTransition[];
    FulfillmentTypeOptions: AdminStoreSalesOrderOption[];
    ActorOptions: AdminStoreSalesOrderOption[];
    ToneOptions: AdminStoreSalesOrderOption[];
  }>("/api/admin/store-order-statuses");
}

export async function updateAdminStoreOrderStatus(StatusKey: string, payload: AdminStoreOrderStatusPayload) {
  return request<{
    Statuses: AdminStoreOrderStatusItem[];
    Transitions: AdminStoreOrderStatusTransition[];
    FulfillmentTypeOptions: AdminStoreSalesOrderOption[];
    ActorOptions: AdminStoreSalesOrderOption[];
    ToneOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/store-order-statuses/${encodeURIComponent(StatusKey)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function createAdminStoreOrderStatus(payload: AdminStoreOrderStatusPayload & { StatusKey: string }) {
  return request<{
    Statuses: AdminStoreOrderStatusItem[];
    Transitions: AdminStoreOrderStatusTransition[];
    FulfillmentTypeOptions: AdminStoreSalesOrderOption[];
    ActorOptions: AdminStoreSalesOrderOption[];
    ToneOptions: AdminStoreSalesOrderOption[];
  }>("/api/admin/store-order-statuses", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createAdminStoreOrderStatusTransition(payload: AdminStoreOrderStatusTransitionPayload) {
  return request<{
    Statuses: AdminStoreOrderStatusItem[];
    Transitions: AdminStoreOrderStatusTransition[];
    FulfillmentTypeOptions: AdminStoreSalesOrderOption[];
    ActorOptions: AdminStoreSalesOrderOption[];
    ToneOptions: AdminStoreSalesOrderOption[];
  }>("/api/admin/store-order-status-transitions", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateAdminStoreOrderStatusTransition(TransitionId: string, payload: AdminStoreOrderStatusTransitionPayload) {
  return request<{
    Statuses: AdminStoreOrderStatusItem[];
    Transitions: AdminStoreOrderStatusTransition[];
    FulfillmentTypeOptions: AdminStoreSalesOrderOption[];
    ActorOptions: AdminStoreSalesOrderOption[];
    ToneOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/store-order-status-transitions/${TransitionId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteAdminStoreOrderStatusTransition(TransitionId: string) {
  return request<{
    Statuses: AdminStoreOrderStatusItem[];
    Transitions: AdminStoreOrderStatusTransition[];
    FulfillmentTypeOptions: AdminStoreSalesOrderOption[];
    ActorOptions: AdminStoreSalesOrderOption[];
    ToneOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/store-order-status-transitions/${TransitionId}`, {
    method: "DELETE",
  });
}

export type AdminStoreOverviewLocationSummary = {
  LocationId: string;
  LocationName: string;
  TodayReservations: number;
  NewReservations: number;
  ServiceRecords: number;
  VoidedServiceRecords: number;
  OrdersPending: number;
  OrdersReady: number;
  OrdersCompleted: number;
  FollowUpsOverdue: number;
  FollowUpsToday: number;
  ScheduleConfiguredThrough: string | null;
};

export type AdminStoreOverviewSummary = {
  Locations: number;
  NewReservations: number;
  ServiceRecords: number;
  VoidedServiceRecords: number;
  OrdersPending: number;
  OrdersReady: number;
  OrdersCompleted: number;
  FollowUpsOverdue: number;
  FollowUpsToday: number;
  ScheduleWarnings: number;
};

export type AdminStoreOverviewParams = {
  DateFrom?: string;
  DateTo?: string;
  LocationIds?: string[];
};

function adminStoreManagementQuery(params: AdminStoreOverviewParams & { Keyword?: string; StoreUserId?: string; ServiceType?: string; RecordStatus?: string; Status?: string; Page?: number; PerPage?: number } = {}) {
  const query = new URLSearchParams();
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.StoreUserId) query.set("StoreUserId", params.StoreUserId);
  if (params.ServiceType) query.set("ServiceType", params.ServiceType);
  if (params.RecordStatus) query.set("RecordStatus", params.RecordStatus);
  if (params.Status) query.set("Status", params.Status);
  if (params.DateFrom) query.set("DateFrom", params.DateFrom);
  if (params.DateTo) query.set("DateTo", params.DateTo);
  params.LocationIds?.forEach((LocationId) => query.append("LocationIds[]", LocationId));
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  return query.toString() ? `?${query.toString()}` : "";
}

export async function getAdminStoreOverview(params: AdminStoreOverviewParams = {}) {
  return request<{
    DateFrom: string;
    DateTo: string;
    Locations: AdminStoreUserLocation[];
    Summary: AdminStoreOverviewSummary;
    LocationsSummary: AdminStoreOverviewLocationSummary[];
  }>(`/api/admin/store-overview${adminStoreManagementQuery(params)}`);
}

export type AdminGoogleCalendarHeadquartersSetting = {
  GoogleCalendarHeadquartersSettingId: string | null;
  GoogleEmail: string;
  CalendarId: string;
  CalendarName: string | null;
  IsEnabled: boolean;
  LastCheckedAt: string | null;
  LastErrorMessage: string | null;
  OAuthGoogleEmail: string | null;
  OAuthScope: string | null;
  TokenExpiresAt: string | null;
  AuthorizedAt: string | null;
  OAuthStatus: "NotConnected" | "Connected" | "Error" | string;
  OAuthLastErrorMessage: string | null;
};

export type AdminStoreGoogleCalendarSetting = {
  StoreGoogleCalendarSettingId: string | null;
  LocationId: string;
  LocationName: string;
  LocationIsActive: boolean;
  GoogleEmail: string;
  CalendarId: string;
  CalendarName: string | null;
  IsEnabled: boolean;
  IncludeInMergedView: boolean;
  LastCheckedAt: string | null;
  LastErrorMessage: string | null;
  OAuthGoogleEmail: string | null;
  OAuthScope: string | null;
  TokenExpiresAt: string | null;
  AuthorizedAt: string | null;
  OAuthStatus: "NotConnected" | "Connected" | "Error" | string;
  OAuthLastErrorMessage: string | null;
};

export async function getAdminStoreGoogleCalendars() {
  return request<{
    HqCalendar: AdminGoogleCalendarHeadquartersSetting;
    Settings: AdminStoreGoogleCalendarSetting[];
  }>("/api/admin/store-google-calendars");
}

export async function updateAdminStoreGoogleCalendars(payload: {
  HqCalendar: Pick<AdminGoogleCalendarHeadquartersSetting, "GoogleEmail" | "CalendarId" | "CalendarName" | "IsEnabled">;
  Settings: Array<Pick<AdminStoreGoogleCalendarSetting, "LocationId" | "GoogleEmail" | "CalendarId" | "CalendarName" | "IsEnabled" | "IncludeInMergedView">>;
}) {
  return request<{
    message?: string;
    HqCalendar: AdminGoogleCalendarHeadquartersSetting;
    Settings: AdminStoreGoogleCalendarSetting[];
  }>("/api/admin/store-google-calendars", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function startAdminStoreGoogleCalendarOAuth(LocationId: string) {
  return request<{ AuthorizationUrl: string }>("/api/admin/store-google-calendars/oauth/start", {
    method: "POST",
    body: JSON.stringify({ LocationId }),
  });
}

export async function startAdminHeadquartersGoogleCalendarOAuth() {
  return request<{ AuthorizationUrl: string }>("/api/admin/store-google-calendars/oauth/start-headquarters", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export type AdminGoogleCalendarTargetType = "Headquarters" | "Store";

export type AdminGoogleCalendarEvent = {
  EventId: string;
  Summary: string;
  Description: string | null;
  Location: string | null;
  ColorId: string | null;
  Attendees: string[];
  HtmlLink: string | null;
  Status: string | null;
  StartAt: string | null;
  EndAt: string | null;
  IsAllDay: boolean;
  ReminderUseDefault: boolean;
  ReminderEnabled: boolean;
  ReminderMethod: "popup" | "email";
  ReminderMinutes: number;
  RecurrenceType: "None" | "Daily" | "Weekly" | "Monthly" | "Yearly";
  RecurrenceUntil: string | null;
  Transparency: "opaque" | "transparent";
  Visibility: "default" | "public" | "private" | "confidential";
  GuestsCanInviteOthers: boolean;
  GuestsCanModify: boolean;
  GuestsCanSeeOtherGuests: boolean;
  HasGoogleMeet: boolean;
  HangoutLink: string | null;
  UpdatedAt: string | null;
};

export type AdminGoogleCalendarEventPayload = {
  TargetType: AdminGoogleCalendarTargetType;
  LocationId?: string;
  Summary: string;
  Description?: string | null;
  Location?: string | null;
  StartAt: string;
  EndAt: string;
  IsAllDay: boolean;
  ColorId?: string | null;
  Attendees?: string | string[];
  ReminderUseDefault?: boolean;
  ReminderEnabled?: boolean;
  ReminderMethod?: "popup" | "email";
  ReminderMinutes?: number;
  RecurrenceType?: "None" | "Daily" | "Weekly" | "Monthly" | "Yearly";
  RecurrenceUntil?: string | null;
  Transparency?: "opaque" | "transparent";
  Visibility?: "default" | "public" | "private" | "confidential";
  GuestsCanInviteOthers?: boolean;
  GuestsCanModify?: boolean;
  GuestsCanSeeOtherGuests?: boolean;
  CreateGoogleMeet?: boolean;
};

function adminGoogleCalendarEventQuery(params: { TargetType: AdminGoogleCalendarTargetType; LocationId?: string; TimeMin?: string; TimeMax?: string }) {
  const query = new URLSearchParams({ TargetType: params.TargetType });
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.TimeMin) query.set("TimeMin", params.TimeMin);
  if (params.TimeMax) query.set("TimeMax", params.TimeMax);
  return query.toString();
}

export async function listAdminGoogleCalendarEvents(params: { TargetType: AdminGoogleCalendarTargetType; LocationId?: string; TimeMin?: string; TimeMax?: string }) {
  return request<{ Events: AdminGoogleCalendarEvent[] }>(`/api/admin/store-google-calendars/events?${adminGoogleCalendarEventQuery(params)}`);
}

export async function createAdminGoogleCalendarEvent(payload: AdminGoogleCalendarEventPayload) {
  return request<{ message?: string; Event: AdminGoogleCalendarEvent }>("/api/admin/store-google-calendars/events", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateAdminGoogleCalendarEvent(eventId: string, payload: AdminGoogleCalendarEventPayload) {
  return request<{ message?: string; Event: AdminGoogleCalendarEvent }>(`/api/admin/store-google-calendars/events/${encodeURIComponent(eventId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteAdminGoogleCalendarEvent(eventId: string, params: { TargetType: AdminGoogleCalendarTargetType; LocationId?: string }) {
  return request<{ message?: string }>(`/api/admin/store-google-calendars/events/${encodeURIComponent(eventId)}?${adminGoogleCalendarEventQuery(params)}`, {
    method: "DELETE",
  });
}

export type AdminStoreReportMetrics = {
  OrderCount: number;
  TotalAmount: number;
  PaidAmount: number;
  PaymentReceived: number;
  BalanceAmount: number;
  AverageOrderAmount: number;
  CompletedOrders: number;
  CancelledOrders: number;
  BalanceDueOrders: number;
  DeliverableOrders: number;
  CompletionRate: number;
  ServiceRecordCount: number;
  FollowUpCreated: number;
  FollowUpCompleted: number;
  FollowUpCompletionRate: number;
};

export type AdminStoreReportLocationSummary = AdminStoreReportMetrics & {
  LocationId: string;
  LocationName: string;
};

export type AdminStoreReportDailyOrder = {
  Date: string;
  OrderCount: number;
  TotalAmount: number;
  PaidAmount: number;
};

export type AdminStoreReportAmountRow = {
  Key: string;
  Label: string;
  Count: number;
  TotalAmount?: number;
  Amount?: number;
};

export type AdminStoreReport = {
  DateFrom: string;
  DateTo: string;
  LocationIds: string[];
  Locations: AdminStoreUserLocation[];
  Metrics: AdminStoreReportMetrics;
  LocationsSummary: AdminStoreReportLocationSummary[];
  DailyOrders: AdminStoreReportDailyOrder[];
  OrderStatuses: AdminStoreReportAmountRow[];
  PaymentMethods: AdminStoreReportAmountRow[];
};

export async function getAdminStoreReport(params: AdminStoreOverviewParams = {}) {
  return request<AdminStoreReport>(`/api/admin/store-reports${adminStoreManagementQuery(params)}`);
}

export async function exportAdminStoreReport(params: AdminStoreOverviewParams = {}) {
  const suffix = adminStoreManagementQuery(params);
  const response = await fetch(`/api/admin/store-reports/export${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(data?.message || "匯出失敗，請稍後再試。");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const fallbackName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const filename = encodedName ? decodeURIComponent(encodedName) : fallbackName || `AdminStoreReport-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export type AdminStorePaymentReconciliationSummary = {
  PaymentCount: number;
  GrossReceived: number;
  RefundAmount: number;
  NetReceived: number;
  PendingCount: number;
  ExceptionCount: number;
  ReviewedCount: number;
  VoidedCount: number;
};

export type AdminStorePaymentReconciliationDaily = {
  Date: string;
  Count: number;
  GrossReceived: number;
  RefundAmount: number;
  NetReceived: number;
};

export type AdminStorePaymentReconciliationMethod = {
  Key: string;
  Label: string;
  Count: number;
  GrossReceived: number;
  RefundAmount: number;
  NetReceived: number;
};

export type AdminStorePaymentReconciliationPayment = {
  StoreSalesOrderPaymentId: string;
  StoreSalesOrderId: string;
  ParentPaymentId?: string | null;
  OrderNumber?: string | null;
  LocationId: string;
  LocationName?: string | null;
  CustomerName?: string | null;
  CustomerPhone?: string | null;
  PaymentType: string;
  PaymentTypeLabel?: string;
  PaymentMethod: string;
  PaymentMethodLabel?: string;
  Amount: number;
  PaymentStatus: "Active" | "Void" | string;
  PaymentStatusLabel: string;
  ReconciliationStatus: "Pending" | "Matched" | "Exception" | "Reviewed" | string;
  ReconciliationStatusLabel: string;
  ReconciliationNote?: string | null;
  IsAbnormal: boolean;
  AbnormalNote?: string | null;
  ReviewedAt?: string | null;
  ReviewedByAdminName?: string | null;
  ReviewNote?: string | null;
  VoidedAt?: string | null;
  VoidReason?: string | null;
  StoreUserName?: string | null;
  Note?: string | null;
  ReceivedAt?: string | null;
  CreatedAt?: string | null;
};

export type AdminStorePaymentReconciliation = {
  DateFrom: string;
  DateTo: string;
  LocationIds: string[];
  Locations: AdminStoreUserLocation[];
  Summary: AdminStorePaymentReconciliationSummary;
  DailySummary: AdminStorePaymentReconciliationDaily[];
  MethodSummary: AdminStorePaymentReconciliationMethod[];
  Payments: AdminStorePaymentReconciliationPayment[];
  Pagination: PaginationMeta;
  PaymentMethodOptions: AdminStoreSalesOrderOption[];
  ReconciliationStatusOptions: AdminStoreSalesOrderOption[];
};

export type AdminStorePaymentReconciliationParams = AdminStoreOverviewParams & {
  PaymentMethod?: string;
  ReconciliationStatus?: string;
  Keyword?: string;
  Page?: number;
  PerPage?: number;
};

function adminStorePaymentReconciliationQuery(params: AdminStorePaymentReconciliationParams = {}) {
  const query = new URLSearchParams();
  if (params.DateFrom) query.set("DateFrom", params.DateFrom);
  if (params.DateTo) query.set("DateTo", params.DateTo);
  if (params.PaymentMethod) query.set("PaymentMethod", params.PaymentMethod);
  if (params.ReconciliationStatus) query.set("ReconciliationStatus", params.ReconciliationStatus);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  params.LocationIds?.forEach((LocationId) => query.append("LocationIds[]", LocationId));
  return query.toString() ? `?${query.toString()}` : "";
}

export async function getAdminStorePaymentReconciliation(params: AdminStorePaymentReconciliationParams = {}) {
  return request<AdminStorePaymentReconciliation>(`/api/admin/store-payment-reconciliation${adminStorePaymentReconciliationQuery(params)}`);
}

export async function exportAdminStorePaymentReconciliation(params: AdminStorePaymentReconciliationParams = {}) {
  const response = await fetch(`/api/admin/store-payment-reconciliation/export${adminStorePaymentReconciliationQuery(params)}`, {
    credentials: "include",
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(data?.message || "匯出失敗，請稍後再試。");
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const fallbackName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const filename = encodedName ? decodeURIComponent(encodedName) : fallbackName || `StorePaymentReconciliation-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function reviewAdminStorePayment(paymentId: string, payload: { ReconciliationStatus: string; ReconciliationNote?: string; IsAbnormal?: boolean; AbnormalNote?: string; ReviewNote?: string }) {
  return request<{ Payment: AdminStorePaymentReconciliationPayment; message?: string }>(`/api/admin/store-payments/${paymentId}/review`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function voidAdminStorePayment(paymentId: string, VoidReason: string) {
  return request<{ message?: string }>(`/api/admin/store-payments/${paymentId}/void`, {
    method: "POST",
    body: JSON.stringify({ VoidReason }),
  });
}

export async function refundAdminStorePayment(paymentId: string, payload: { Amount: number; Reason: string }) {
  return request<{ message?: string }>(`/api/admin/store-payments/${paymentId}/refund`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type AdminStoreCustomer = {
  CustomerId: string;
  CustomerCode: string;
  MemberId: string | null;
  Name: string;
  Phone: string | null;
  Email: string | null;
  Gender?: string | null;
  Birthday?: string | null;
  AddressCity?: string | null;
  AddressDistrict?: string | null;
  AddressPostalCode?: string | null;
  AddressLine?: string | null;
  PreferredLocationId?: string | null;
  PreferredLocationName: string | null;
  Status: string;
  IsBlacklisted: boolean;
  Note: string | null;
  SourceType?: string | null;
  MemberName?: string | null;
  MemberEmail?: string | null;
  MemberPhone?: string | null;
  LastContactAt: string | null;
  LastOrderAt: string | null;
  LastServiceAt: string | null;
  RelatedLocationCount: number;
  OrderCount: number;
  TotalOrderAmount: number;
  ServiceRecordCount: number;
  InteractionCount: number;
  UpdatedAt: string | null;
};

export type AdminStoreCustomerDetail = AdminStoreCustomer & {
  Locations: Array<Record<string, unknown>>;
  Identities: Array<Record<string, unknown>>;
  Activities: Array<Record<string, unknown>>;
  Orders: Array<Record<string, unknown>>;
  ServiceRecords: Array<Record<string, unknown>>;
  OwnedProducts: Array<Record<string, unknown>>;
  Tickets: Array<Record<string, unknown>>;
  MergeLogs: Array<Record<string, unknown>>;
  DuplicateCandidates: AdminStoreCustomer[];
};

export type AdminStoreCustomerListParams = {
  Keyword?: string;
  Status?: string;
  MemberState?: string;
  LocationIds?: string[];
  Page?: number;
  PerPage?: number;
};

export type AdminStoreCustomerPayload = {
  Name: string;
  Phone?: string | null;
  Email?: string | null;
  Gender?: string | null;
  Birthday?: string | null;
  AddressCity?: string | null;
  AddressDistrict?: string | null;
  AddressPostalCode?: string | null;
  AddressLine?: string | null;
  PreferredLocationId?: string | null;
  Status: string;
  IsBlacklisted: boolean;
  Note?: string | null;
};

export async function listAdminStoreCustomers(params: AdminStoreCustomerListParams = {}) {
  return request<{
    Customers: AdminStoreCustomer[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    Locations: AdminStoreUserLocation[];
  }>(`/api/admin/store-customers${adminStoreManagementQuery(params)}`);
}

export async function getAdminStoreCustomer(CustomerId: string) {
  return request<{ Customer: AdminStoreCustomerDetail }>(`/api/admin/store-customers/${CustomerId}`);
}

export async function updateAdminStoreCustomer(CustomerId: string, payload: AdminStoreCustomerPayload) {
  return request<{ Customer: AdminStoreCustomerDetail; message?: string }>(`/api/admin/store-customers/${CustomerId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function mergeAdminStoreCustomer(CustomerId: string, payload: { FromCustomerId: string; Reason?: string }) {
  return request<{ Customer: AdminStoreCustomerDetail; message?: string }>(`/api/admin/store-customers/${CustomerId}/merge`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type AdminStoreServiceRecord = {
  ServiceRecordId: string;
  LocationId: string;
  LocationName: string | null;
  StoreUserId: string | null;
  StoreUserName: string | null;
  CustomerName: string;
  CustomerPhone: string;
  VisitDate: string;
  ServiceType: string;
  ServiceTypeLabel: string;
  Summary: string | null;
  Recommendation: string | null;
  NextFollowUpAt: string | null;
  TrialProductName: string | null;
  TrialSerialNumber: string | null;
  TrialEarSide: string | null;
  Status: "Active" | "Voided" | string;
  VoidedAt: string | null;
  VoidReason: string | null;
  CreatedAt: string | null;
  UpdatedAt: string | null;
  TrialRecords?: Array<Record<string, unknown>>;
};

export type AdminStoreServiceRecordListParams = {
  Keyword?: string;
  LocationIds?: string[];
  StoreUserId?: string;
  ServiceType?: string;
  RecordStatus?: string;
  DateFrom?: string;
  DateTo?: string;
  Page?: number;
  PerPage?: number;
};

export async function listAdminStoreServiceRecords(params: AdminStoreServiceRecordListParams = {}) {
  return request<{
    ServiceRecords: AdminStoreServiceRecord[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    Locations: AdminStoreUserLocation[];
    StoreUsers: AdminStoreActivityLogUser[];
    ServiceTypeOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/store-service-records${adminStoreManagementQuery(params)}`);
}

export async function getAdminStoreServiceRecord(ServiceRecordId: string) {
  return request<{ ServiceRecord: AdminStoreServiceRecord }>(`/api/admin/store-service-records/${ServiceRecordId}`);
}

export type AdminStoreFollowUp = {
  FollowUpId: string;
  LocationId: string | null;
  LocationName: string | null;
  StoreUserId: string | null;
  StoreUserName: string | null;
  SourceType: string | null;
  SourceId: string | null;
  CustomerName: string;
  CustomerPhone: string;
  Subject: string;
  NextFollowUpAt: string | null;
  Status: string;
  StatusLabel: string;
  CompletedAt: string | null;
  CompletedNote: string | null;
  PostponedAt: string | null;
  PostponedNote: string | null;
  CreatedAt: string | null;
  IsOverdue: boolean;
  IsToday: boolean;
};

export type AdminStoreFollowUpListParams = {
  Keyword?: string;
  LocationIds?: string[];
  StoreUserId?: string;
  Status?: string;
  DateFrom?: string;
  DateTo?: string;
  Page?: number;
  PerPage?: number;
};

export async function listAdminStoreFollowUps(params: AdminStoreFollowUpListParams = {}) {
  return request<{
    FollowUps: AdminStoreFollowUp[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    Locations: AdminStoreUserLocation[];
    StoreUsers: AdminStoreActivityLogUser[];
    StatusOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/store-follow-ups${adminStoreManagementQuery(params)}`);
}

export type AdminStoreActivityLogOption = { Key: string; Label: string };
export type AdminStoreActivityLogUser = { StoreUserId: string; DisplayName: string; Username: string };
export type AdminStoreActivityLog = {
  ActivityLogId: string;
  StoreUserId: string | null;
  StoreUsername?: string | null;
  StoreUserName?: string | null;
  LocationId: string | null;
  LocationName?: string | null;
  EntityType: string;
  EntityTypeLabel: string;
  EntityId: string;
  Action: string;
  ActionLabel: string;
  BeforeData?: Record<string, unknown> | null;
  AfterData?: Record<string, unknown> | null;
  IpAddress?: string | null;
  UserAgent?: string | null;
  CreatedAt: string;
};

export type AdminStoreActivityLogListParams = {
  Keyword?: string;
  LocationIds?: string[];
  StoreUserId?: string;
  EntityType?: string;
  Action?: string;
  DateFrom?: string;
  DateTo?: string;
  Page?: number;
  PerPage?: number;
};

function adminStoreActivityLogQuery(params: AdminStoreActivityLogListParams = {}) {
  const query = new URLSearchParams();
  if (params.Keyword) query.set("Keyword", params.Keyword);
  params.LocationIds?.forEach((LocationId) => query.append("LocationIds[]", LocationId));
  if (params.StoreUserId) query.set("StoreUserId", params.StoreUserId);
  if (params.EntityType) query.set("EntityType", params.EntityType);
  if (params.Action) query.set("Action", params.Action);
  if (params.DateFrom) query.set("DateFrom", params.DateFrom);
  if (params.DateTo) query.set("DateTo", params.DateTo);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  return query.toString() ? `?${query.toString()}` : "";
}

export async function listAdminStoreActivityLogs(params: AdminStoreActivityLogListParams = {}) {
  return request<{
    Logs: AdminStoreActivityLog[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    Locations: AdminStoreUserLocation[];
    StoreUsers: AdminStoreActivityLogUser[];
    EntityTypeOptions: AdminStoreActivityLogOption[];
    ActionOptions: AdminStoreActivityLogOption[];
  }>(`/api/admin/store-activity-logs${adminStoreActivityLogQuery(params)}`);
}

export type AdminStoreAnnouncementCategory = "Announcement" | "SOP" | "Promotion" | "Policy";
export type AdminStoreAnnouncementPriority = "Normal" | "High";
export type AdminStoreAnnouncementAudienceType = "All" | "Locations";

export type AdminStoreAnnouncement = {
  StoreAnnouncementId: string;
  Title: string;
  Category: AdminStoreAnnouncementCategory;
  CategoryLabel: string;
  Priority: AdminStoreAnnouncementPriority;
  PriorityLabel: string;
  AudienceType: AdminStoreAnnouncementAudienceType;
  AudienceTypeLabel: string;
  Body: string;
  IsPinned: boolean;
  IsActive: boolean;
  PublishedAt: string | null;
  ExpiresAt: string | null;
  LocationIds: string[];
  Locations: AdminStoreUserLocation[];
  ReadCount: number;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type AdminStoreAnnouncementPayload = {
  Title: string;
  Category: AdminStoreAnnouncementCategory;
  Priority: AdminStoreAnnouncementPriority;
  AudienceType: AdminStoreAnnouncementAudienceType;
  Body: string;
  IsPinned: boolean;
  IsActive: boolean;
  PublishedAt?: string | null;
  ExpiresAt?: string | null;
  LocationIds: string[];
};

export type AdminStoreAnnouncementListParams = {
  Status?: string;
  Category?: string;
  Keyword?: string;
  LocationIds?: string[];
  Page?: number;
  PerPage?: number;
};

function adminStoreAnnouncementQuery(params: AdminStoreAnnouncementListParams = {}) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Category) query.set("Category", params.Category);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  params.LocationIds?.forEach((LocationId) => query.append("LocationIds[]", LocationId));
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  return query.toString() ? `?${query.toString()}` : "";
}

export async function listAdminStoreAnnouncements(params: AdminStoreAnnouncementListParams = {}) {
  return request<{
    Items: AdminStoreAnnouncement[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    Locations: AdminStoreUserLocation[];
    CategoryOptions: { Key: string; Label: string }[];
    PriorityOptions: { Key: string; Label: string }[];
  }>(`/api/admin/store-announcements${adminStoreAnnouncementQuery(params)}`);
}

export async function createAdminStoreAnnouncement(payload: AdminStoreAnnouncementPayload) {
  return request<{ Item: AdminStoreAnnouncement }>("/api/admin/store-announcements", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateAdminStoreAnnouncement(StoreAnnouncementId: string, payload: AdminStoreAnnouncementPayload) {
  return request<{ Item: AdminStoreAnnouncement }>(`/api/admin/store-announcements/${StoreAnnouncementId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteAdminStoreAnnouncement(StoreAnnouncementId: string) {
  return request<Record<string, never>>(`/api/admin/store-announcements/${StoreAnnouncementId}`, { method: "DELETE" });
}

export type AdminErpMetrics = {
  PendingEngineering: number;
  WaitingPurchase: number;
  PreparingOutbound: number;
  ShippedToStore: number;
  ReadyForPickup: number;
  SkuCount: number;
  LegacyStockTotal: number;
  ErpInStockTotal: number;
  ErpReservedTotal: number;
  LowStockCount: number;
  OutOfStockCount: number;
  PurchaseOrderOpen: number;
  OutboundOrderOpen: number;
  OverduePurchaseOrders: number;
  PendingPurchaseReceipts: number;
  ReceiptExceptions: number;
  PendingPayables: number;
  OverduePayables: number;
  PayableBalanceAmount: number;
};

export type AdminErpFlowItem = { Key: string; Label: string; Count: number };
export type AdminErpWarehouse = {
  ErpWarehouseId: string;
  Code: string;
  Name: string;
  WarehouseType: string;
  LocationId: string | null;
  IsActive: boolean;
};

export type AdminErpDashboard = {
  Metrics: AdminErpMetrics;
  StoreOrderFlow: AdminErpFlowItem[];
  Warehouses: AdminErpWarehouse[];
};

export async function getAdminErpDashboard() {
  return request<AdminErpDashboard>("/api/admin/erp/dashboard");
}

export type AdminErpInventoryItem = {
  ProductId: string;
  VariantId: string | null;
  ProductName: string;
  VariantLabel: string | null;
  Sku: string;
  ProductCode?: string;
  LegacyStock: number;
  ErpInStock: number;
  ErpReserved: number;
  ErpOutbound: number;
  ErpDelivered?: number;
  PendingInspectionStock?: number;
  QuarantinedStock?: number;
  TransferOutStock?: number;
  AvailableStock: number;
  IncomingStock?: number;
  PurchaseIncoming?: number;
  TransferIncoming?: number;
  OutboundOpen?: number;
  SafetyStock?: number;
  ReorderPoint?: number;
  MaxStock?: number;
  LeadTimeDays?: number;
  NeedsReorder?: boolean;
  IsLowStock?: boolean;
  IsOutOfStock?: boolean;
  IsOverStock?: boolean;
  SuggestedPurchaseQuantity?: number;
  ReorderSuggestionSource?: string;
  AverageCost?: number;
  StockValue?: number;
  WarehouseId?: string | null;
  Source: string;
  SourceKey?: string;
};

export type AdminErpInventoryParams = {
  Keyword?: string;
  StockLevel?: string;
  ErpWarehouseId?: string;
  Source?: string;
  Alert?: string;
  Page?: number;
  PerPage?: number;
};

function adminErpInventoryQuery(params: AdminErpInventoryParams = {}) {
  const query = new URLSearchParams();
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.StockLevel) query.set("StockLevel", params.StockLevel);
  if (params.ErpWarehouseId) query.set("ErpWarehouseId", params.ErpWarehouseId);
  if (params.Source) query.set("Source", params.Source);
  if (params.Alert) query.set("Alert", params.Alert);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  return query.toString() ? `?${query.toString()}` : "";
}

export async function listAdminErpInventory(params: AdminErpInventoryParams = {}) {
  return request<{
    Items: AdminErpInventoryItem[];
    Counts: { Total: number; Available: number; Low: number; Out: number; PendingInspection?: number; Quarantined?: number };
    Pagination: PaginationMeta;
    Warehouses: AdminErpWarehouse[];
    StockLevelOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/erp/inventory${adminErpInventoryQuery(params)}`);
}

export type AdminErpStoreOrderCandidate = {
  StoreSalesOrderId: string;
  OrderNumber: string;
  Status: string;
  StatusLabel: string;
  OrderPurpose?: string | null;
  OrderPurposeLabel?: string | null;
  ZeroPriceReason?: string | null;
  ZeroPriceReasonLabel?: string | null;
  ZeroPriceNote?: string | null;
  LocationId: string | null;
  LocationName: string | null;
  CustomerName: string;
  CustomerPhone: string;
  ProductSummary: string;
  Items?: Array<{
    StoreSalesOrderItemId: string;
    ProductId: string;
    VariantId: string | null;
    Sku: string | null;
    ProductName: string;
    VariantLabel: string | null;
    Quantity: number;
    DealPrice: number;
    IncomingQuantity?: number;
    SuggestedPurchaseQuantity?: number;
    RecommendedSupplier?: {
      ErpSupplierId: string;
      SupplierCode?: string | null;
      SupplierName?: string | null;
      SupplierSku?: string | null;
      PurchaseUnit: string;
      UnitCost: number;
      Currency: string;
      MinimumOrderQuantity: number;
      LeadTimeDays: number;
      PaymentTerms?: string | null;
      IsDefault?: boolean;
    } | null;
    SuggestedSupplierPrices?: Array<{
      ErpSupplierProductPriceId: string;
      ErpSupplierId: string;
      SupplierCode?: string | null;
      SupplierName?: string | null;
      SupplierSku?: string | null;
      PurchaseUnit: string;
      UnitCost: number;
      Currency: string;
      MinimumOrderQuantity: number;
      LeadTimeDays: number;
      PaymentTerms?: string | null;
      IsDefault?: boolean;
    }>;
    AvailableStockUnits?: Array<{
      ErpStockUnitId: string;
      SerialNumber: string | null;
      EarSide: string | null;
      EarSideLabel: string;
      WarehouseName: string | null;
      UnitCost: number;
      ReceivedAt: string | null;
      Note: string | null;
    }>;
  }>;
  TotalAmount: number;
  BalanceAmount: number;
  CreatedAt: string | null;
};

export type AdminErpPurchaseOrder = {
  ErpPurchaseOrderId: string;
  PurchaseNumber: string;
  RevisionNo?: number;
  ErpSupplierId?: string | null;
  SupplierCode?: string | null;
  SupplierName: string | null;
  SupplierMasterName?: string | null;
  ExternalOrderNumber?: string | null;
  Currency?: string;
  ExchangeRate?: number;
  PurchaseTotal?: number;
  Status: string;
  StatusLabel: string;
  ExpectedArrivalDate: string | null;
  OrderedAt: string | null;
  ApprovedAt?: string | null;
  ApprovedByAdminName?: string | null;
  ApprovalNote?: string | null;
  ReceivedAt: string | null;
  CancelledAt?: string | null;
  CancelReason?: string | null;
  RevisionNote?: string | null;
  StoreSalesOrderId: string | null;
  StoreOrderNumber: string | null;
  SourceOrderPurpose?: string | null;
  SourceOrderPurposeLabel?: string | null;
  SourceZeroPriceReason?: string | null;
  SourceZeroPriceReasonLabel?: string | null;
  SourceZeroPriceNote?: string | null;
  SourceOrders?: Array<{ StoreSalesOrderId: string; OrderNumber?: string | null; CustomerName?: string | null; LocationName?: string | null; OrderPurpose?: string | null; OrderPurposeLabel?: string | null; ZeroPriceReason?: string | null; ZeroPriceReasonLabel?: string | null; ZeroPriceNote?: string | null }>;
  CreatedByAdminName: string | null;
  ItemCount: number;
  QuantityTotal: number;
  ArrivedQuantityTotal?: number;
  ReceivedQuantityTotal?: number;
  Items?: Array<{
    ErpPurchaseOrderItemId: string;
    ProductId: string;
    VariantId: string | null;
    ProductCode?: string | null;
    Sku: string | null;
    SupplierSku?: string | null;
    ProductName: string;
    VariantLabel: string | null;
    PurchaseUnit?: string;
    Quantity: number;
    UnitCost: number;
    ArrivedQuantity?: number;
    ReceivedQuantity: number;
    RemainingQuantity: number;
    Note: string | null;
  }>;
  Receipts?: Array<{
    ErpPurchaseReceiptId: string;
    ReceiptNumber: string;
    ReceivedAt: string;
    WarehouseName: string | null;
    QuantityTotal: number;
    InspectionStatus?: string;
    InspectionStatusLabel?: string;
    ArrivedQuantity?: number;
    AcceptedQuantity?: number;
    RejectedQuantity?: number;
    ExceptionSummary?: string | null;
    ResolutionAction?: string | null;
    ResolutionActionLabel?: string | null;
    Note: string | null;
  }>;
  Note: string | null;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type AdminErpOutboundOrder = {
  ErpOutboundOrderId: string;
  OutboundNumber: string;
  StoreSalesOrderId: string | null;
  StoreOrderNumber: string | null;
  OrderPurpose?: string | null;
  OrderPurposeLabel?: string | null;
  ZeroPriceReason?: string | null;
  ZeroPriceReasonLabel?: string | null;
  ZeroPriceNote?: string | null;
  FromWarehouseName: string | null;
  ToLocationName: string | null;
  Status: string;
  StatusLabel: string;
  ShippedAt: string | null;
  ArrivedAt: string | null;
  EngineeringInspection?: {
    InspectionNumber: string;
    InspectionStatus: string;
    Checklist: Record<string, boolean>;
    AbnormalNote?: string | null;
    ResolutionNote?: string | null;
    InspectedAt?: string | null;
  } | null;
  PickingChecklist?: Record<string, boolean>;
  PackingChecklist?: Record<string, boolean>;
  LogisticsCarrier?: string | null;
  TrackingNumber?: string | null;
  LogisticsNote?: string | null;
  LogisticsUpdatedAt?: string | null;
  StoreSignedAt?: string | null;
  StoreSignedNote?: string | null;
  StoreSignedByAdminName?: string | null;
  CreatedByAdminName: string | null;
  ItemCount: number;
  QuantityTotal: number;
  Items?: Array<{
    ErpOutboundOrderItemId: string;
    ProductId: string;
    VariantId: string | null;
    Sku: string | null;
    ProductName: string;
    VariantLabel: string | null;
    Quantity: number;
    StoreSalesOrderItemId?: string | null;
    ErpStockUnitId?: string | null;
    SerialNumber: string | null;
    EarSide: string | null;
    EarSideLabel: string;
    WarrantyStartDate?: string | null;
    Note: string | null;
  }>;
  Note: string | null;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type AdminErpOrderParams = {
  Keyword?: string;
  Status?: string;
  Page?: number;
  PerPage?: number;
};

function adminErpOrderQuery(params: AdminErpOrderParams = {}) {
  const query = new URLSearchParams();
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Status) query.set("Status", params.Status);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  return query.toString() ? `?${query.toString()}` : "";
}

export async function listAdminErpPurchaseOrders(params: AdminErpOrderParams = {}) {
  return request<{
    Orders: AdminErpPurchaseOrder[];
    Candidates: AdminErpStoreOrderCandidate[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    StatusOptions: AdminStoreSalesOrderOption[];
  }>(`/api/admin/erp/purchase-orders${adminErpOrderQuery(params)}`);
}

export async function listAdminErpOutboundOrders(params: AdminErpOrderParams = {}) {
  return request<{
    Orders: AdminErpOutboundOrder[];
    Candidates: AdminErpStoreOrderCandidate[];
    Counts: Record<string, number>;
    Pagination: PaginationMeta;
    StatusOptions: AdminStoreSalesOrderOption[];
    Warehouses: AdminErpWarehouse[];
  }>(`/api/admin/erp/outbound-orders${adminErpOrderQuery(params)}`);
}

export type AdminMenuItemType = "Group" | "Page" | "External";

export type AdminMenuItem = {
  AdminMenuItemId: string;
  ParentId: string | null;
  ItemType: AdminMenuItemType;
  Label: string;
  Icon: string | null;
  Path: string | null;
  PermissionKey: string | null;
  PermissionLabel: string | null;
  SortOrder: number;
  IsActive: boolean;
  /** 只有 /api/admin/my-menu（AdminLayout 用）才會回傳這個欄位；選單維護頁面（/api/admin/menu-items）不會有。 */
  IsAllowed?: boolean;
};

export type AdminMenuItemPayload = {
  ItemType: AdminMenuItemType;
  Label: string;
  Icon?: string;
  ParentId?: string | null;
  Path?: string;
  PermissionKey?: string;
  SortOrder?: number;
  IsActive?: boolean;
};

/** AdminLayout 用：取得目前登入帳號能看到的選單樹（已依權限標好 IsAllowed）。 */
export async function fetchMyMenu() {
  return request<{ IsSuperAdmin: boolean; Items: AdminMenuItem[] }>("/api/admin/my-menu");
}

/** 選單維護頁面（僅超級管理者可用）：取得完整選單樹（含停用項目）＋權限目錄。 */
export async function listMenuItems() {
  return request<{ Items: AdminMenuItem[]; PermissionCatalog: AdminPermissionCatalogItem[] }>("/api/admin/menu-items");
}

export async function createMenuItem(payload: AdminMenuItemPayload) {
  return request<{ Item: AdminMenuItem }>("/api/admin/menu-items", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateMenuItem(AdminMenuItemId: string, payload: Partial<AdminMenuItemPayload>) {
  return request<{ Item: AdminMenuItem }>(`/api/admin/menu-items/${AdminMenuItemId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteMenuItem(AdminMenuItemId: string) {
  return request<Record<string, never>>(`/api/admin/menu-items/${AdminMenuItemId}`, {
    method: "DELETE",
  });
}

export type AdminOrderListParams = {
  OrderStatus?: string;
  PaymentStatus?: string;
  Keyword?: string;
  StartDate?: string;
  EndDate?: string;
  Page?: number;
  PerPage?: number;
};

export async function listAdminOrders(params: AdminOrderListParams) {
  const query = new URLSearchParams();
  if (params.OrderStatus) query.set("OrderStatus", params.OrderStatus);
  if (params.PaymentStatus) query.set("PaymentStatus", params.PaymentStatus);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.StartDate) query.set("StartDate", params.StartDate);
  if (params.EndDate) query.set("EndDate", params.EndDate);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Orders: Order[];
    Counts?: Record<OrderStatus, number>;
    PaymentCounts?: Record<PaymentStatus, number>;
    Pagination: PaginationMeta;
  }>(`/api/admin/orders${suffix}`);
}

/** 匯出 Excel（.xlsx），跟前端既有的「匯出 CSV」（downloadOrdersCsv，純前端組字串）並存，欄位完全一致。 */
export async function exportAdminOrders(params: Pick<AdminOrderListParams, "OrderStatus" | "PaymentStatus" | "Keyword" | "StartDate" | "EndDate">) {
  const query = new URLSearchParams();
  if (params.OrderStatus) query.set("OrderStatus", params.OrderStatus);
  if (params.PaymentStatus) query.set("PaymentStatus", params.PaymentStatus);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.StartDate) query.set("StartDate", params.StartDate);
  if (params.EndDate) query.set("EndDate", params.EndDate);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(`/api/admin/orders/export${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(data?.message || "匯出失敗，請稍後再試。");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const matched = disposition.match(/filename="?([^";]+)"?/i);
  const filename = matched?.[1] || `Orders-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function getAdminOrder(orderId: string) {
  return request<{ Order: Order }>(`/api/admin/orders/${orderId}`);
}

export async function updateAdminOrder(orderId: string, payload: { OrderStatus?: OrderStatus; PaymentStatus?: PaymentStatus; AdminNote?: string }) {
  return request<{ Order: Order }>(`/api/admin/orders/${orderId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** 訂單列表「多筆選取」批次操作：一次對多筆訂單套用同一個訂單狀態／付款狀態。 */
export async function bulkUpdateAdminOrders(payload: { OrderIds: string[]; OrderStatus?: OrderStatus; PaymentStatus?: PaymentStatus }) {
  return request<{ UpdatedCount: number; SkippedCount: number }>(`/api/admin/orders/bulk-update`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** 退款紀錄，每一筆都是獨立、不可修改刪除的財務紀錄（比照一般會計慣例，見後端 Refund entity 說明）。 */
export type AdminRefund = {
  RefundId: string;
  OrderId: string;
  /** 只有「退款管理」總覽頁（跨訂單）才會有值；單一訂單詳情頁的退款歷史不需要，本來就在那筆訂單的頁面上。 */
  OrderNumber?: string | null;
  RecipientName?: string | null;
  Amount: number;
  Reason: string | null;
  OperatorName: string | null;
  CreatedAt: string | null;
};

/** 單一訂單詳情頁：查詢這筆訂單的退款歷史 + 目前還可以退多少錢。 */
export async function listOrderRefunds(orderId: string) {
  return request<{ Refunds: AdminRefund[]; RefundableAmount: number }>(`/api/admin/orders/${orderId}/refunds`);
}

/** 單一訂單詳情頁：新增一筆退款（支援部分退款），成功後訂單的 RefundedAmount／PaymentStatus 會自動同步更新。 */
export async function createOrderRefund(orderId: string, payload: { Amount: number; Reason?: string }) {
  return request<{ message: string; Refund: AdminRefund; RefundedAmount: number; PaymentStatus: PaymentStatus }>(
    `/api/admin/orders/${orderId}/refunds`,
    { method: "POST", body: JSON.stringify(payload) }
  );
}

export type AdminRefundListParams = {
  StartDate?: string;
  EndDate?: string;
  Keyword?: string;
  Page?: number;
  PerPage?: number;
};

/** 「退款管理」總覽頁：跨訂單、依時間區間查詢所有退款紀錄。 */
export async function listAdminRefunds(params: AdminRefundListParams) {
  const query = new URLSearchParams();
  if (params.StartDate) query.set("StartDate", params.StartDate);
  if (params.EndDate) query.set("EndDate", params.EndDate);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Refunds: AdminRefund[];
    Summary: { Count: number; Total: number };
    Pagination: PaginationMeta;
  }>(`/api/admin/refunds${suffix}`);
}

export type FinanceReportGroupBy = "day" | "month";

export type FinanceReportSummary = {
  OrderCount: number;
  SubtotalAmount: number;
  DiscountAmount: number;
  ShippingFeeTotal: number;
  PaymentExtraFeeTotal: number;
  GrossRevenue: number;
  RefundCount: number;
  RefundTotal: number;
  NetRevenue: number;
};

export type FinanceReportTrendPoint = {
  Period: string;
  GrossRevenue: number;
  OrderCount: number;
  RefundTotal: number;
  NetRevenue: number;
};

export type FinanceReportGroupedTotal = { Label: string; OrderCount: number; TotalAmount: number };

export type FinanceReportOrderStatusTotal = { Status: OrderStatus; OrderCount: number; TotalAmount: number };

export type FinanceReport = {
  Range: { StartDate: string; EndDate: string; GroupBy: FinanceReportGroupBy };
  Summary: FinanceReportSummary;
  Trend: FinanceReportTrendPoint[];
  ByPaymentMethod: FinanceReportGroupedTotal[];
  ByShippingMethod: FinanceReportGroupedTotal[];
  ByOrderStatus: FinanceReportOrderStatusTotal[];
};

/** 「財務報表」頁：依時間區間彙總營收、退款、淨營收，以及依付款方式／配送方式／訂單狀態的分布。 */
export async function getFinanceReport(params: { StartDate?: string; EndDate?: string; GroupBy?: FinanceReportGroupBy }) {
  const query = new URLSearchParams();
  if (params.StartDate) query.set("StartDate", params.StartDate);
  if (params.EndDate) query.set("EndDate", params.EndDate);
  if (params.GroupBy) query.set("GroupBy", params.GroupBy);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<FinanceReport>(`/api/admin/finance-reports${suffix}`);
}

/** 匯出趨勢資料為 Excel（.xlsx），跟前端既有的「匯出趨勢 CSV」（downloadTrendCsv，純前端組字串）並存，欄位完全一致。 */
export async function exportFinanceReportTrend(params: { StartDate?: string; EndDate?: string; GroupBy?: FinanceReportGroupBy }) {
  const query = new URLSearchParams();
  if (params.StartDate) query.set("StartDate", params.StartDate);
  if (params.EndDate) query.set("EndDate", params.EndDate);
  if (params.GroupBy) query.set("GroupBy", params.GroupBy);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(`/api/admin/finance-reports/export${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(data?.message || "匯出失敗，請稍後再試。");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const matched = disposition.match(/filename="?([^";]+)"?/i);
  const filename = matched?.[1] || `FinanceReport-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export type PaymentTransactionStatus = "Created" | "Success" | "Failed";

export type AdminPaymentTransaction = {
  PaymentTransactionId: string;
  OrderId: string;
  OrderNumber?: string | null;
  RecipientName?: string | null;
  GatewayProvider: string | null;
  TradeNumber: string;
  GatewayTransactionId: string | null;
  Amount: number;
  Status: PaymentTransactionStatus;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type AdminPaymentTransactionListParams = {
  StartDate?: string;
  EndDate?: string;
  Status?: string;
  GatewayProvider?: string;
  Keyword?: string;
  Page?: number;
  PerPage?: number;
};

/** 「金流交易紀錄查詢」總覽頁：跨訂單查詢所有線上金流交易嘗試紀錄（含重試）。 */
export async function listPaymentTransactions(params: AdminPaymentTransactionListParams) {
  const query = new URLSearchParams();
  if (params.StartDate) query.set("StartDate", params.StartDate);
  if (params.EndDate) query.set("EndDate", params.EndDate);
  if (params.Status) query.set("Status", params.Status);
  if (params.GatewayProvider) query.set("GatewayProvider", params.GatewayProvider);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{ Transactions: AdminPaymentTransaction[]; Pagination: PaginationMeta }>(`/api/admin/payment-transactions${suffix}`);
}

export type InvoiceProvider = "ECPay" | "ezPay";

export type InvoiceSetting = {
  InvoiceSettingId: string;
  Provider: InvoiceProvider | null;
  ConfigStatus: Record<string, boolean>;
  Mode: string | null;
  IsConfigured: boolean;
  AutoIssueOnPaid: boolean;
  IsEnabled: boolean;
  IsReady: boolean;
  UpdatedAt: string | null;
};

export async function getInvoiceSetting() {
  return request<{ Item: InvoiceSetting }>("/api/admin/invoice-setting");
}

export type InvoiceSettingPayload = {
  Provider?: InvoiceProvider | "";
  Config?: { MerchantId?: string; HashKey?: string; HashIV?: string; Mode?: "Test" | "Production" };
  AutoIssueOnPaid?: boolean;
  IsEnabled?: boolean;
};

export async function updateInvoiceSetting(payload: InvoiceSettingPayload) {
  return request<{ message: string; Item: InvoiceSetting }>("/api/admin/invoice-setting", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export type InvoiceStatus = "Issued" | "Failed" | "Voided";

export type AdminInvoice = {
  InvoiceId: string;
  OrderId: string;
  Provider: string;
  InvoiceNumber: string | null;
  RandomNumber: string | null;
  Amount: number;
  BuyerType: string;
  BuyerUBN: string | null;
  BuyerName: string | null;
  Status: InvoiceStatus;
  FailReason: string | null;
  IssuedAt: string | null;
  VoidedAt: string | null;
  VoidReason: string | null;
  CreatedAt: string | null;
};

export async function listOrderInvoices(orderId: string) {
  return request<{ Invoices: AdminInvoice[] }>(`/api/admin/orders/${orderId}/invoices`);
}

export async function issueOrderInvoice(orderId: string) {
  return request<{ message: string; Invoice: AdminInvoice }>(`/api/admin/orders/${orderId}/invoices/issue`, { method: "POST" });
}

export async function voidOrderInvoice(orderId: string, invoiceId: string, reason: string) {
  return request<{ message: string; Invoice: AdminInvoice }>(`/api/admin/orders/${orderId}/invoices/${invoiceId}/void`, {
    method: "POST",
    body: JSON.stringify({ Reason: reason }),
  });
}

export type AdminMember = {
  MemberId: string;
  Email: string;
  Name: string;
  Gender?: string | null;
  BirthDate?: string | null;
  Phone?: string | null;
  AddressCity?: string | null;
  AddressDistrict?: string | null;
  AddressPostalCode?: string | null;
  AddressLine?: string | null;
  IsEmailVerified: boolean;
  IsActive: boolean;
  LastLoginAt?: string | null;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
  OrderCount?: number;
  TotalSpent?: number;
  PaidAmount?: number;
  UnpaidAmount?: number;
  RefundedAmount?: number;
};

export type AdminMemberUpdatePayload = {
  IsActive?: boolean;
  Name?: string;
  Email?: string;
  Phone?: string;
  Gender?: string;
  BirthDate?: string;
  AddressCity?: string;
  AddressDistrict?: string;
  AddressPostalCode?: string;
  AddressLine?: string;
};

export type AdminMemberListParams = {
  Status?: string;
  Verified?: string;
  Keyword?: string;
  Page?: number;
  PerPage?: number;
};

export async function listAdminMembers(params: AdminMemberListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Verified) query.set("Verified", params.Verified);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Members: AdminMember[];
    Counts?: { ActiveStatus: { Active: number; Inactive: number }; VerifiedStatus: { Verified: number; Unverified: number } };
    Pagination: PaginationMeta;
  }>(`/api/admin/members${suffix}`);
}

export async function getAdminMember(memberId: string) {
  return request<{ Member: AdminMember }>(`/api/admin/members/${memberId}`);
}

export async function updateAdminMember(memberId: string, payload: AdminMemberUpdatePayload) {
  return request<{ Member: AdminMember }>(`/api/admin/members/${memberId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function verifyAdminMember(memberId: string) {
  return request<{ Member: AdminMember }>(`/api/admin/members/${memberId}/verify`, {
    method: "POST",
  });
}

export async function resendAdminMemberVerification(memberId: string) {
  return request<Record<string, never>>(`/api/admin/members/${memberId}/resend-verification`, {
    method: "POST",
  });
}

export async function sendAdminMemberPasswordReset(memberId: string) {
  return request<Record<string, never>>(`/api/admin/members/${memberId}/send-password-reset`, {
    method: "POST",
  });
}

export async function setAdminMemberPassword(memberId: string, newPassword: string) {
  return request<{ Member: AdminMember }>(`/api/admin/members/${memberId}/password`, {
    method: "PATCH",
    body: JSON.stringify({ NewPassword: newPassword }),
  });
}

export type AdminTeamMember = {
  TeamMemberId: string;
  Name: string;
  Title: string;
  Description: string;
  ImageUrl?: string | null;
  IsActive: boolean;
  SortOrder: number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminTeamMemberPayload = {
  Name: string;
  Title: string;
  Description: string;
  ImageUrl?: string;
  IsActive: boolean;
  SortOrder: number;
};

export async function listAdminTeamMembers(params: ListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Items: AdminTeamMember[];
    Counts: { Active: number; Inactive: number };
    Pagination: PaginationMeta;
  }>(`/api/admin/team-members${suffix}`);
}

export async function saveAdminTeamMember(teamMemberId: string | null, payload: AdminTeamMemberPayload) {
  const path = teamMemberId ? `/api/admin/team-members/${teamMemberId}` : "/api/admin/team-members";
  const method = teamMemberId ? "PUT" : "POST";
  return request<{ Item: AdminTeamMember }>(path, { method, body: JSON.stringify(payload) });
}

export async function deleteAdminTeamMember(teamMemberId: string) {
  return request<Record<string, never>>(`/api/admin/team-members/${teamMemberId}`, { method: "DELETE" });
}

export async function uploadAdminTeamMemberImage(file: File) {
  const formData = new FormData();
  formData.append("Image", file);
  const response = await fetch("/api/admin/team-members/upload-image", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", ...(getCsrfToken() ? { "X-CSRF-Token": getCsrfToken() } : {}) },
    body: formData,
  });
  const data = (await response.json().catch(() => ({}))) as { ok: boolean; Url?: string; message?: string };
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "圖片上傳失敗，請稍後再試。");
  }
  return data.Url || "";
}

export async function deleteAdminTeamMemberImage(url: string) {
  return request<Record<string, never>>("/api/admin/team-members/upload-image", {
    method: "DELETE",
    body: JSON.stringify({ Url: url }),
  });
}

export type AdminTestimonial = {
  TestimonialId: string;
  CustomerName: string;
  CustomerHandle?: string | null;
  Content: string;
  Rating: number;
  AvatarUrl?: string | null;
  IsVerified: boolean;
  IsActive: boolean;
  SortOrder: number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminTestimonialPayload = {
  CustomerName: string;
  CustomerHandle?: string;
  Content: string;
  Rating: number;
  AvatarUrl?: string;
  IsVerified: boolean;
  IsActive: boolean;
  SortOrder: number;
};

export async function listAdminTestimonials(params: ListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Items: AdminTestimonial[];
    Counts: { Active: number; Inactive: number };
    Pagination: PaginationMeta;
  }>(`/api/admin/testimonials${suffix}`);
}

export async function saveAdminTestimonial(testimonialId: string | null, payload: AdminTestimonialPayload) {
  const path = testimonialId ? `/api/admin/testimonials/${testimonialId}` : "/api/admin/testimonials";
  const method = testimonialId ? "PUT" : "POST";
  return request<{ Item: AdminTestimonial }>(path, { method, body: JSON.stringify(payload) });
}

export async function deleteAdminTestimonial(testimonialId: string) {
  return request<Record<string, never>>(`/api/admin/testimonials/${testimonialId}`, { method: "DELETE" });
}

export async function uploadAdminTestimonialImage(file: File) {
  const formData = new FormData();
  formData.append("Image", file);
  const response = await fetch("/api/admin/testimonials/upload-image", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", ...(getCsrfToken() ? { "X-CSRF-Token": getCsrfToken() } : {}) },
    body: formData,
  });
  const data = (await response.json().catch(() => ({}))) as { ok: boolean; Url?: string; message?: string };
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "圖片上傳失敗，請稍後再試。");
  }
  return data.Url || "";
}

export async function deleteAdminTestimonialImage(url: string) {
  return request<Record<string, never>>("/api/admin/testimonials/upload-image", {
    method: "DELETE",
    body: JSON.stringify({ Url: url }),
  });
}

export type OperationLocationDisplayPage = "About" | "Contact";

export type AdminOperationLocation = {
  LocationId: string;
  CountryCode: string;
  CountryName: string;
  Name: string;
  Tag: string;
  IsHeadquarters: boolean;
  AddressCity: string;
  AddressDistrict: string;
  AddressPostalCode?: string | null;
  AddressLine: string;
  Phone: string;
  BusinessHours: string[];
  DisplayOn: OperationLocationDisplayPage[];
  IsActive: boolean;
  SortOrder: number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminOperationLocationPayload = {
  CountryCode: string;
  CountryName: string;
  Name: string;
  Tag: string;
  IsHeadquarters: boolean;
  AddressCity: string;
  AddressDistrict: string;
  AddressPostalCode?: string;
  AddressLine: string;
  Phone: string;
  BusinessHours: string[];
  DisplayOn: OperationLocationDisplayPage[];
  IsActive: boolean;
  SortOrder: number;
};

export type AdminOperationLocationListParams = ListParams & { CountryCode?: string };

export async function listAdminOperationLocations(params: AdminOperationLocationListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.CountryCode) query.set("CountryCode", params.CountryCode);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Items: AdminOperationLocation[];
    Counts: { Active: number; Inactive: number };
    Countries: { CountryCode: string; CountryName: string }[];
    Pagination: PaginationMeta;
  }>(`/api/admin/operation-locations${suffix}`);
}

export async function saveAdminOperationLocation(locationId: string | null, payload: AdminOperationLocationPayload) {
  const path = locationId ? `/api/admin/operation-locations/${locationId}` : "/api/admin/operation-locations";
  const method = locationId ? "PUT" : "POST";
  return request<{ Item: AdminOperationLocation }>(path, { method, body: JSON.stringify(payload) });
}

export async function deleteAdminOperationLocation(locationId: string) {
  return request<Record<string, never>>(`/api/admin/operation-locations/${locationId}`, { method: "DELETE" });
}

export type AdminNewsletterSubscriber = {
  NewsletterSubscriberId: string;
  Email: string;
  IsActive: boolean;
  SourcePage?: string | null;
  SubscribedAt?: string | null;
  UnsubscribedAt?: string | null;
  Member?: { MemberId: string; Name: string } | null;
};

export async function listAdminNewsletterSubscribers(params: ListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Items: AdminNewsletterSubscriber[];
    Counts: { Active: number; Inactive: number };
    Pagination: PaginationMeta;
  }>(`/api/admin/newsletter-subscribers${suffix}`);
}

export async function deleteAdminNewsletterSubscriber(subscriberId: string) {
  return request<Record<string, never>>(`/api/admin/newsletter-subscribers/${subscriberId}`, { method: "DELETE" });
}

export async function exportAdminNewsletterSubscribers() {
  const response = await fetch("/api/admin/newsletter-subscribers/export", {
    credentials: "include",
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(data?.message || "匯出失敗，請稍後再試。");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const matched = disposition.match(/filename="?([^";]+)"?/i);
  const filename = matched?.[1] || `NewsletterSubscribers-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export type NewsletterCampaignStatus = "Draft" | "Sending" | "Sent";

export type AdminNewsletterCampaign = {
  CampaignId: string;
  Subject: string;
  PreviewText?: string | null;
  HtmlContent: string;
  Status: NewsletterCampaignStatus;
  IsEditable: boolean;
  TotalRecipients: number;
  SentCount: number;
  FailedCount: number;
  SentAt?: string | null;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminNewsletterCampaignPayload = {
  Subject: string;
  PreviewText?: string;
  HtmlContent: string;
};

export type NewsletterRecipientCounts = { Pending: number; Sent: number; Failed: number };

export async function listAdminNewsletterCampaigns(params: ListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Items: AdminNewsletterCampaign[];
    Counts: { Draft: number; Sending: number; Sent: number };
    Pagination: PaginationMeta;
  }>(`/api/admin/newsletter-campaigns${suffix}`);
}

export async function getAdminNewsletterCampaign(campaignId: string) {
  return request<{ Item: AdminNewsletterCampaign; RecipientCounts: NewsletterRecipientCounts }>(
    `/api/admin/newsletter-campaigns/${campaignId}`
  );
}

export async function saveAdminNewsletterCampaign(campaignId: string | null, payload: AdminNewsletterCampaignPayload) {
  const path = campaignId ? `/api/admin/newsletter-campaigns/${campaignId}` : "/api/admin/newsletter-campaigns";
  const method = campaignId ? "PUT" : "POST";
  return request<{ Item: AdminNewsletterCampaign }>(path, { method, body: JSON.stringify(payload) });
}

export async function deleteAdminNewsletterCampaign(campaignId: string) {
  return request<Record<string, never>>(`/api/admin/newsletter-campaigns/${campaignId}`, { method: "DELETE" });
}

export async function sendAdminNewsletterCampaignTest(campaignId: string, email: string) {
  return request<Record<string, never>>(`/api/admin/newsletter-campaigns/${campaignId}/send-test`, {
    method: "POST",
    body: JSON.stringify({ Email: email }),
  });
}

export async function startAdminNewsletterCampaign(campaignId: string) {
  return request<{ Item: AdminNewsletterCampaign }>(`/api/admin/newsletter-campaigns/${campaignId}/start`, {
    method: "POST",
  });
}

export async function processAdminNewsletterCampaignBatch(campaignId: string) {
  return request<{
    Item: AdminNewsletterCampaign;
    RecipientCounts: NewsletterRecipientCounts;
    ProcessedInBatch: number;
    Done: boolean;
  }>(`/api/admin/newsletter-campaigns/${campaignId}/process-batch`, { method: "POST" });
}

export async function retryAdminNewsletterCampaignFailed(campaignId: string) {
  return request<{ Item: AdminNewsletterCampaign; ResetCount: number }>(
    `/api/admin/newsletter-campaigns/${campaignId}/retry-failed`,
    { method: "POST" }
  );
}

export type NewsletterCampaignRecipientStatus = "Pending" | "Sent" | "Failed";

export type AdminNewsletterCampaignRecipient = {
  RecipientId: string;
  Email: string;
  Status: NewsletterCampaignRecipientStatus;
  SentAt?: string | null;
  ErrorMessage?: string | null;
};

export async function listAdminNewsletterCampaignRecipients(
  campaignId: string,
  params: { Status?: string; Keyword?: string; Page?: number; PerPage?: number }
) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Items: AdminNewsletterCampaignRecipient[];
    RecipientCounts: NewsletterRecipientCounts;
    Pagination: PaginationMeta;
  }>(`/api/admin/newsletter-campaigns/${campaignId}/recipients${suffix}`);
}

export async function uploadAdminNewsletterCampaignImage(file: File) {
  const formData = new FormData();
  formData.append("Image", file);
  const response = await fetch("/api/admin/newsletter-campaigns/upload-image", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", ...(getCsrfToken() ? { "X-CSRF-Token": getCsrfToken() } : {}) },
    body: formData,
  });
  const data = (await response.json().catch(() => ({}))) as { ok: boolean; Url?: string; message?: string };
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "圖片上傳失敗，請稍後再試。");
  }
  return data.Url || "";
}

export type AdminStaticPage = {
  StaticPageId: string;
  Slug: string;
  Title: string;
  Content: string;
  IsPublished: boolean;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminStaticPagePayload = {
  Slug: string;
  Title: string;
  Content: string;
  IsPublished: boolean;
};

export async function listAdminStaticPages(params: ListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Items: AdminStaticPage[];
    Counts: { Published: number; Hidden: number };
    Pagination: PaginationMeta;
  }>(`/api/admin/static-pages${suffix}`);
}

export async function getAdminStaticPage(staticPageId: string) {
  return request<{ Item: AdminStaticPage }>(`/api/admin/static-pages/${staticPageId}`);
}

export async function saveAdminStaticPage(staticPageId: string | null, payload: AdminStaticPagePayload) {
  const path = staticPageId ? `/api/admin/static-pages/${staticPageId}` : "/api/admin/static-pages";
  const method = staticPageId ? "PUT" : "POST";
  return request<{ Item: AdminStaticPage }>(path, { method, body: JSON.stringify(payload) });
}

export async function deleteAdminStaticPage(staticPageId: string) {
  return request<Record<string, never>>(`/api/admin/static-pages/${staticPageId}`, { method: "DELETE" });
}

export async function uploadAdminStaticPageImage(file: File) {
  const formData = new FormData();
  formData.append("Image", file);
  const response = await fetch("/api/admin/static-pages/upload-image", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", ...(getCsrfToken() ? { "X-CSRF-Token": getCsrfToken() } : {}) },
    body: formData,
  });
  const data = (await response.json().catch(() => ({}))) as { ok: boolean; Url?: string; message?: string };
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "圖片上傳失敗，請稍後再試。");
  }
  return data.Url || "";
}

export type AdminCompanyProfile = {
  CompanyProfileId: string;
  CompanyName: string;
  ServicePhone: string;
  ServicePhoneNote?: string | null;
  ServiceHours: string;
  ServiceEmail: string;
  AddressCity: string;
  AddressDistrict: string;
  AddressPostalCode?: string | null;
  AddressLine: string;
  ContactPhone?: string | null;
  SmsPhone?: string | null;
  Copyright?: string | null;
  LineId?: string | null;
  LineUrl?: string | null;
  FacebookUrl?: string | null;
  InstagramUrl?: string | null;
  YoutubeUrl?: string | null;
  LogoUrl?: string | null;
  FaviconUrl?: string | null;
  BookingPublicLookaheadDays?: number;
  IsMaintenanceMode?: boolean;
  MaintenanceMessage?: string | null;
  UpdatedAt?: string | null;
};

export type AdminCompanyProfilePayload = {
  CompanyName: string;
  ServicePhone: string;
  ServicePhoneNote?: string;
  ServiceHours: string;
  ServiceEmail: string;
  AddressCity: string;
  AddressDistrict: string;
  AddressPostalCode?: string;
  AddressLine: string;
  ContactPhone?: string;
  SmsPhone?: string;
  Copyright?: string;
  LineId?: string;
  LineUrl?: string;
  FacebookUrl?: string;
  InstagramUrl?: string;
  YoutubeUrl?: string;
  LogoUrl?: string;
  FaviconUrl?: string;
  BookingPublicLookaheadDays?: number;
  IsMaintenanceMode?: boolean;
  MaintenanceMessage?: string;
};

export async function getAdminCompanyProfile() {
  return request<{ CompanyProfile: AdminCompanyProfile }>("/api/admin/company-profile");
}

export async function saveAdminCompanyProfile(payload: AdminCompanyProfilePayload) {
  return request<{ CompanyProfile: AdminCompanyProfile }>("/api/admin/company-profile", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export type AdminBookingLocation = {
  LocationId: string;
  Name: string;
  Phone?: string;
  Address?: string;
};

export type AdminBookingSlot = {
  Time: string;
  Capacity: number;
  Booked: number;
  Available: boolean;
};

export type AdminBookingAvailability = {
  BookingAvailabilityId?: string | null;
  LocationId: string;
  LocationName: string;
  AppointmentDate: string;
  IsOpen: boolean;
  Slots: AdminBookingSlot[];
  RawSlots: string[];
  SlotCapacity: number;
  IsNationalHoliday: boolean;
  Note?: string | null;
  UpdatedAt?: string | null;
};

export type AdminBookingAvailabilityPayload = {
  LocationId: string;
  AppointmentDate: string;
  IsOpen: boolean;
  Slots: string[];
  SlotCapacity: number;
  IsNationalHoliday?: boolean;
  Note?: string;
  ApplyToAllLocations?: boolean;
};

export async function listAdminBookingAvailability(params: { LocationId?: string; StartDate?: string; EndDate?: string }) {
  const query = new URLSearchParams();
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.StartDate) query.set("StartDate", params.StartDate);
  if (params.EndDate) query.set("EndDate", params.EndDate);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Locations: AdminBookingLocation[];
    SelectedLocationId: string | null;
    Items: AdminBookingAvailability[];
    BookingPublicLookaheadDays?: number;
    Coverage?: {
      ConfiguredThrough: string | null;
      DaysRemaining: number | null;
      WarningThresholdDays: number;
    };
  }>(`/api/admin/booking-availability${suffix}`);
}

export async function saveAdminBookingAvailability(payload: AdminBookingAvailabilityPayload) {
  return request<{ Item: AdminBookingAvailability }>("/api/admin/booking-availability", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function bulkGenerateAdminBookingAvailability(payload: {
  LocationId: string;
  StartDate: string;
  EndDate: string;
  Weekdays: number[];
  Slots: string[];
  SlotCapacity: number;
  Overwrite: boolean;
  ApplyToAllLocations?: boolean;
}) {
  return request<{ Created: number; Updated: number; Skipped: number }>("/api/admin/booking-availability/bulk-generate", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function uploadAdminCompanyProfileBrandImage(file: File) {
  const formData = new FormData();
  formData.append("Image", file);
  const response = await fetch("/api/admin/company-profile/upload-brand-image", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", ...(getCsrfToken() ? { "X-CSRF-Token": getCsrfToken() } : {}) },
    body: formData,
  });
  const data = (await response.json().catch(() => ({}))) as { ok: boolean; Url?: string; message?: string };
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "圖片上傳失敗，請稍後再試。");
  }
  return data.Url || "";
}

export type AdminHomeSection = {
  HomeSectionId: string;
  SectionKey: string;
  AdminTitle: string;
  Eyebrow: string | null;
  Title: string;
  Subtitle: string | null;
  Description: string | null;
  BadgeText: string | null;
  ImageUrl: string | null;
  MobileImageUrl: string | null;
  ImageAlt: string | null;
  PrimaryButtonLabel: string | null;
  PrimaryButtonUrl: string | null;
  SecondaryButtonLabel: string | null;
  SecondaryButtonUrl: string | null;
  NoteText: string | null;
  Tags: string[];
  IsActive: boolean;
  SortOrder: number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminHomeSectionPayload = Omit<AdminHomeSection, "HomeSectionId" | "SectionKey" | "CreatedAt" | "UpdatedAt">;

export async function listAdminHomeSections() {
  return request<{ Items: AdminHomeSection[] }>("/api/admin/home-sections");
}

export async function updateAdminHomeSection(homeSectionId: string, payload: AdminHomeSectionPayload) {
  return request<{ Item: AdminHomeSection }>(`/api/admin/home-sections/${homeSectionId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function uploadAdminHomeSectionImage(file: File) {
  const formData = new FormData();
  formData.append("Image", file);
  const response = await fetch("/api/admin/home-sections/upload-image", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", ...(getCsrfToken() ? { "X-CSRF-Token": getCsrfToken() } : {}) },
    body: formData,
  });
  const data = (await response.json().catch(() => ({}))) as { ok: boolean; Url?: string; message?: string };
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "圖片上傳失敗");
  }
  return data.Url || "";
}

export type AdminDashboardRevenuePoint = {
  Date: string;
  Revenue: number;
  OrderCount: number;
};

export type AdminDashboardRecentOrder = {
  OrderId: string;
  OrderNumber: string;
  RecipientName: string;
  TotalAmount: number;
  OrderStatus: string;
  PaymentStatus: string;
  CreatedAt: string | null;
};

export type AdminDashboardRecentFathersDay = {
  ReservationId: string;
  Name: string;
  Store: string;
  Status: string;
  CreatedAt: string | null;
};

export type AdminDashboardRecentProductTrial = {
  ProductTrialReservationId: string;
  Name: string;
  Store: string;
  ProductName: string;
  Status: string;
  CreatedAt: string | null;
};

export type AdminDashboardRecentMessage = {
  ContactMessageId: string;
  Name: string;
  Subject: string;
  Status: string;
  CreatedAt: string | null;
};

export type AdminDashboardRecentCustomerServiceTicket = {
  TicketId: string;
  TicketNumber: string;
  Subject: string;
  Priority: string;
  Status: string;
  CustomerName: string | null;
  SourceType: string;
  SourceId: string | null;
  NextFollowUpAt: string | null;
  UpdatedAt: string | null;
};

export type AdminDashboardRecentStoreSalesOrder = {
  StoreSalesOrderId: string;
  OrderNumber: string;
  LocationId: string;
  LocationName: string;
  StoreUserName: string | null;
  CustomerName: string;
  CustomerPhone: string;
  CustomerEmail: string | null;
  Status: string;
  StatusLabel: string;
  PaymentStatus: string;
  PaymentStatusLabel: string;
  TotalAmount: number;
  BalanceAmount: number;
  FirstProductName: string | null;
  FirstVariantLabel: string | null;
  ItemCount: number;
  CreatedAt: string | null;
};

export type AdminDashboardCampaign = {
  CampaignId: string;
  Subject: string;
  Status: string;
  TotalRecipients: number;
  SentCount: number;
  FailedCount: number;
  SentAt: string | null;
  CreatedAt: string | null;
};

export type AdminDashboard = {
  Orders: {
    CountByStatus: { Pending: number; Processing: number; Shipped: number; Completed: number; Cancelled: number };
    TodayCount: number;
    RevenueToday: number;
    RevenueThisMonth: number;
    RevenueLastMonth: number;
    Trend: AdminDashboardRevenuePoint[];
  };
  Reservations: {
    FathersDay: { New: number; Contacted: number; Booked: number; Closed: number };
    ProductTrial: { New: number; Contacted: number; Booked: number; Closed: number };
    PendingTotal: number;
  };
  ContactMessages: { New: number; Replied: number; Closed: number };
  CustomerService: {
    Status: {
      New: number;
      InProgress: number;
      WaitingCustomer: number;
      WaitingInternal: number;
      Resolved: number;
      Closed: number;
    };
    OpenTotal: number;
    Overdue: number;
  };
  Members: {
    Total: number;
    ActiveStatus: { Active: number; Inactive: number };
    VerifiedStatus: { Verified: number; Unverified: number };
    NewToday: number;
    NewThisWeek: number;
    NewThisMonth: number;
  };
  Products: {
    StatusCounts: { Published: number; Hidden: number };
    LowStockCount: number;
    OutOfStockCount: number;
  };
  Newsletter: {
    SubscriberCounts: { Active: number; Inactive: number };
    LastCampaign: AdminDashboardCampaign | null;
  };
  BookingAvailability: {
    ConfiguredThrough: string | null;
    DaysRemaining: number | null;
    WarningThresholdDays: number;
    PublicLookaheadDays: number;
    NeedsAttention: boolean;
  } | null;
  StoreOrders: {
    Total: number;
    TodayCount: number;
    ThisMonthCount: number;
    RevenueThisMonth: number;
    PendingHeadquarters: number;
    PendingEngineering: number;
    WaitingPurchase: number;
    PreparingOutbound: number;
    ShippedToStore: number;
    ReadyForPickup: number;
    BalanceDue: number;
    UnnotifiedArrived: number;
    CompletedThisMonth: number;
  };
  ErpOperations: {
    OverduePurchases: number;
    PendingReceipts: number;
    OpenRma: number;
    PendingPayables: number;
    OpenTasks: number;
  };
  Recent: {
    Orders: AdminDashboardRecentOrder[];
    StoreSalesOrders: AdminDashboardRecentStoreSalesOrder[];
    FathersDayReservations: AdminDashboardRecentFathersDay[];
    ProductTrialReservations: AdminDashboardRecentProductTrial[];
    ContactMessages: AdminDashboardRecentMessage[];
    CustomerServiceTickets: AdminDashboardRecentCustomerServiceTicket[];
  };
};

export async function getAdminDashboard() {
  return request<AdminDashboard>("/api/admin/dashboard");
}

export type ShippingDeliveryType = "HomeDelivery" | "StorePickup" | "ConvenienceStorePickup";
export type LogisticsProvider = "ECPay";
export type LogisticsMode = "Test" | "Production";
export type EcpayLogisticsSubType = "UNIMART" | "FAMI" | "HILIFE" | "UNIMARTC2C" | "FAMIC2C" | "HILIFEC2C" | "OKMARTC2C";

export type AdminShippingMethod = {
  ShippingMethodId: string;
  Code: string;
  Name: string;
  Description: string | null;
  DeliveryType: ShippingDeliveryType;
  LogisticsProvider: LogisticsProvider | null;
  LogisticsSubType: EcpayLogisticsSubType | null;
  IsLogisticsCollection: boolean;
  RequiresStoreMap: boolean;
  BaseFee: number;
  FreeShippingThreshold: number | null;
  IsActive: boolean;
  SortOrder: number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminShippingMethodPayload = {
  Code: string;
  Name: string;
  Description?: string;
  DeliveryType: ShippingDeliveryType;
  LogisticsProvider?: LogisticsProvider | "";
  LogisticsSubType?: EcpayLogisticsSubType | "";
  IsLogisticsCollection?: boolean;
  RequiresStoreMap?: boolean;
  BaseFee: number;
  FreeShippingThreshold?: number | null;
  IsActive: boolean;
  SortOrder: number;
};

export async function listAdminShippingMethods() {
  return request<{ Items: AdminShippingMethod[] }>("/api/admin/shipping-methods");
}

export async function saveAdminShippingMethod(shippingMethodId: string | null, payload: AdminShippingMethodPayload) {
  const path = shippingMethodId ? `/api/admin/shipping-methods/${shippingMethodId}` : "/api/admin/shipping-methods";
  const method = shippingMethodId ? "PUT" : "POST";
  return request<{ Item: AdminShippingMethod }>(path, { method, body: JSON.stringify(payload) });
}

export async function deleteAdminShippingMethod(shippingMethodId: string) {
  return request<Record<string, never>>(`/api/admin/shipping-methods/${shippingMethodId}`, { method: "DELETE" });
}

export type LogisticsSetting = {
  LogisticsSettingId: string | null;
  Provider: LogisticsProvider;
  Mode: LogisticsMode;
  ConfigStatus: Record<"MerchantId" | "HashKey" | "HashIV", boolean>;
  SenderName: string | null;
  SenderPhone: string | null;
  SenderCellPhone: string | null;
  SenderZipCode: string | null;
  SenderAddress: string | null;
  ServerReplyUrl: string | null;
  ClientReplyUrl: string | null;
  IsEnabled: boolean;
  IsStoreMapEnabled: boolean;
  IsConfigured: boolean;
  IsReady: boolean;
  UpdatedAt: string | null;
};

export type LogisticsSettingPayload = {
  Provider: LogisticsProvider;
  Mode: LogisticsMode;
  Config: Partial<Record<"MerchantId" | "HashKey" | "HashIV", string>>;
  SenderName: string;
  SenderPhone: string;
  SenderCellPhone: string;
  SenderZipCode: string;
  SenderAddress: string;
  ServerReplyUrl: string;
  ClientReplyUrl: string;
  IsEnabled: boolean;
  IsStoreMapEnabled: boolean;
};

export async function getLogisticsSetting() {
  return request<{ Item: LogisticsSetting; Carriers?: AdminLogisticsCarrier[] }>("/api/admin/logistics-setting");
}

export async function updateLogisticsSetting(payload: LogisticsSettingPayload) {
  return request<{ Item: LogisticsSetting; message?: string }>("/api/admin/logistics-setting", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export type AdminLogisticsCarrier = {
  LogisticsCarrierId: string;
  Code: string;
  Name: string;
  ContactName: string | null;
  ContactPhone: string | null;
  TrackingUrlTemplate: string | null;
  Note: string | null;
  IsActive: boolean;
  SortOrder: number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminLogisticsCarrierPayload = {
  Code: string;
  Name: string;
  ContactName?: string;
  ContactPhone?: string;
  TrackingUrlTemplate?: string;
  Note?: string;
  IsActive: boolean;
  SortOrder: number;
};

export async function listAdminLogisticsCarriers() {
  return request<{ Items: AdminLogisticsCarrier[] }>("/api/admin/logistics-carriers");
}

export async function saveAdminLogisticsCarrier(logisticsCarrierId: string | null, payload: AdminLogisticsCarrierPayload) {
  const path = logisticsCarrierId ? `/api/admin/logistics-carriers/${logisticsCarrierId}` : "/api/admin/logistics-carriers";
  const method = logisticsCarrierId ? "PUT" : "POST";
  return request<{ Item: AdminLogisticsCarrier; message?: string }>(path, {
    method,
    body: JSON.stringify(payload),
  });
}

export async function deleteAdminLogisticsCarrier(logisticsCarrierId: string) {
  return request<{ message?: string }>(`/api/admin/logistics-carriers/${logisticsCarrierId}`, { method: "DELETE" });
}

export type AdminShippingRule = {
  ShippingRuleId: string;
  Name: string;
  ShippingMethodId: string | null;
  ShippingMethodName: string | null;
  MinOrderAmount: number | null;
  FeeAmount: number;
  Priority: number;
  StartAt: string | null;
  EndAt: string | null;
  IsActive: boolean;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminShippingRulePayload = {
  Name: string;
  ShippingMethodId?: string | null;
  MinOrderAmount?: number | null;
  FeeAmount: number;
  Priority: number;
  StartAt?: string | null;
  EndAt?: string | null;
  IsActive: boolean;
};

export async function listAdminShippingRules() {
  return request<{ Items: AdminShippingRule[] }>("/api/admin/shipping-rules");
}

export async function saveAdminShippingRule(shippingRuleId: string | null, payload: AdminShippingRulePayload) {
  const path = shippingRuleId ? `/api/admin/shipping-rules/${shippingRuleId}` : "/api/admin/shipping-rules";
  const method = shippingRuleId ? "PUT" : "POST";
  return request<{ Item: AdminShippingRule }>(path, { method, body: JSON.stringify(payload) });
}

export async function deleteAdminShippingRule(shippingRuleId: string) {
  return request<Record<string, never>>(`/api/admin/shipping-rules/${shippingRuleId}`, { method: "DELETE" });
}

export const COUPON_DISCOUNT_TYPES = ["FreeShipping", "FixedAmount", "Percentage"] as const;
export type CouponDiscountType = (typeof COUPON_DISCOUNT_TYPES)[number];

export type AdminShippingCoupon = {
  ShippingCouponId: string;
  Code: string;
  Description: string | null;
  DiscountType: CouponDiscountType;
  /** FixedAmount：折抵金額（元）。Percentage：萬分比（例如 1000 代表 10%）。 */
  DiscountAmount: number;
  MaxDiscountAmount: number | null;
  MinOrderAmount: number | null;
  MaxRedemptions: number | null;
  RedemptionCount: number;
  StartAt: string | null;
  EndAt: string | null;
  IsActive: boolean;
  BatchId: string | null;
  BatchLabel: string | null;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminShippingCouponPayload = {
  Code: string;
  Description?: string;
  DiscountType: CouponDiscountType;
  DiscountAmount?: number;
  MaxDiscountAmount?: number | null;
  MinOrderAmount?: number | null;
  MaxRedemptions?: number | null;
  StartAt?: string | null;
  EndAt?: string | null;
  IsActive: boolean;
};

export async function listAdminShippingCoupons(params: ListParams) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{ Items: AdminShippingCoupon[]; Pagination: PaginationMeta }>(`/api/admin/shipping-coupons${suffix}`);
}

export async function saveAdminShippingCoupon(shippingCouponId: string | null, payload: AdminShippingCouponPayload) {
  const path = shippingCouponId ? `/api/admin/shipping-coupons/${shippingCouponId}` : "/api/admin/shipping-coupons";
  const method = shippingCouponId ? "PUT" : "POST";
  return request<{ Item: AdminShippingCoupon }>(path, { method, body: JSON.stringify(payload) });
}

export async function deleteAdminShippingCoupon(shippingCouponId: string) {
  return request<Record<string, never>>(`/api/admin/shipping-coupons/${shippingCouponId}`, { method: "DELETE" });
}

/** 免運優惠碼「系統自動配置模式」：一次產生 N 組不重複的代碼，全部歸屬同一個批次，供活動大量發放使用。 */
export type AdminShippingCouponBatchPayload = {
  BatchLabel: string;
  Quantity: number;
  CodeLength?: number;
  CodePrefix?: string;
  DiscountType: CouponDiscountType;
  DiscountAmount?: number;
  MaxDiscountAmount?: number | null;
  MaxRedemptionsPerCode?: number | null;
  MinOrderAmount?: number | null;
  StartAt?: string | null;
  EndAt?: string | null;
  IsActive: boolean;
};

export type AdminShippingCouponBatchSummary = {
  BatchId: string;
  BatchLabel: string | null;
  TotalCodes: number;
  UsedCodes: number;
  ActiveCodes: number;
  CreatedAt: string;
};

export async function generateAdminShippingCouponBatch(payload: AdminShippingCouponBatchPayload) {
  return request<{ BatchId: string; BatchLabel: string; Items: AdminShippingCoupon[] }>("/api/admin/shipping-coupons/batches", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listAdminShippingCouponBatches() {
  return request<{ Batches: AdminShippingCouponBatchSummary[] }>("/api/admin/shipping-coupons/batches");
}

export async function getAdminShippingCouponBatch(batchId: string) {
  return request<{ BatchId: string; BatchLabel: string | null; Items: AdminShippingCoupon[] }>(`/api/admin/shipping-coupons/batches/${batchId}`);
}

export async function deleteAdminShippingCouponBatch(batchId: string) {
  return request<{ DeletedCount: number }>(`/api/admin/shipping-coupons/batches/${batchId}`, { method: "DELETE" });
}

/** 匯出批次代碼為 Excel（.xlsx），跟前端既有的「下載 CSV」（downloadCouponsCsv，純前端組字串）並存，欄位完全一致。 */
export async function exportAdminShippingCouponBatch(batchId: string) {
  const response = await fetch(`/api/admin/shipping-coupons/batches/${batchId}/export`, {
    credentials: "include",
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(data?.message || "匯出失敗，請稍後再試。");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const matched = disposition.match(/filename="?([^";]+)"?/i);
  const filename = matched?.[1] || `coupons-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const PAYMENT_GATEWAY_PROVIDERS = ["ECPay", "NewebPay"] as const;
export type PaymentGatewayProvider = (typeof PAYMENT_GATEWAY_PROVIDERS)[number];

/** 各金流商需要填寫的 GatewayConfig 欄位（比照後端 PaymentMethod::GATEWAY_REQUIRED_CONFIG_KEYS）。 */
export const PAYMENT_GATEWAY_CONFIG_FIELDS: Record<PaymentGatewayProvider, { key: string; label: string; placeholder?: string }[]> = {
  ECPay: [
    { key: "MerchantId", label: "特店編號 MerchantID" },
    { key: "HashKey", label: "HashKey" },
    { key: "HashIV", label: "HashIV" },
    { key: "Mode", label: "環境", placeholder: "Test 或 Production" },
    { key: "ChoosePayment", label: "付款方式代碼（選填）", placeholder: "預設 ALL" },
  ],
  NewebPay: [
    { key: "MerchantId", label: "商店代號 MerchantID" },
    { key: "HashKey", label: "HashKey" },
    { key: "HashIV", label: "HashIV" },
    { key: "Mode", label: "環境", placeholder: "Test 或 Production" },
  ],
};

export type AdminPaymentMethod = {
  PaymentMethodId: string;
  Code: string;
  Name: string;
  Description: string | null;
  /** 訂單成立後、仍為未付款狀態時顯示給客人的付款說明（例如銀行轉帳的帳號資訊），跟結帳頁的 Description 是不同欄位。 */
  Instructions: string | null;
  SettlementType: "Manual" | "Gateway";
  GatewayProvider: PaymentGatewayProvider | null;
  /** 每個必要金鑰欄位是否已經有值，不含任何原始金鑰內容（後端刻意遮罩，見 PaymentMethodApiController）。 */
  GatewayConfigStatus: Record<string, boolean>;
  GatewayMode: string | null;
  IsGatewayReady: boolean;
  ExtraFeeType: "None" | "Fixed" | "Percentage";
  ExtraFeeAmount: number;
  PaymentDueDays: number | null;
  IsActive: boolean;
  SortOrder: number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type AdminPaymentMethodPayload = {
  Code: string;
  Name: string;
  Description?: string;
  Instructions?: string;
  SettlementType: "Manual" | "Gateway";
  GatewayProvider?: PaymentGatewayProvider | "";
  /** 留空的欄位代表「保留原本已設定的值」，後端採合併寫入，絕不會把既有金鑰清空（見 PaymentMethodApiController::applyGatewayConfig）。 */
  GatewayConfig?: Record<string, string>;
  ExtraFeeType: "None" | "Fixed" | "Percentage";
  ExtraFeeAmount: number;
  PaymentDueDays?: number | null;
  IsActive: boolean;
  SortOrder: number;
};

export async function listAdminPaymentMethods() {
  return request<{ Items: AdminPaymentMethod[] }>("/api/admin/payment-methods");
}

export async function saveAdminPaymentMethod(paymentMethodId: string | null, payload: AdminPaymentMethodPayload) {
  const path = paymentMethodId ? `/api/admin/payment-methods/${paymentMethodId}` : "/api/admin/payment-methods";
  const method = paymentMethodId ? "PUT" : "POST";
  return request<{ Item: AdminPaymentMethod }>(path, { method, body: JSON.stringify(payload) });
}

export async function deleteAdminPaymentMethod(paymentMethodId: string) {
  return request<Record<string, never>>(`/api/admin/payment-methods/${paymentMethodId}`, { method: "DELETE" });
}
